/**
 * AIInterpreterPanel - AI-powered email parsing interface
 * EF-AI-EMAIL-INTERPRETER-V2 + EF-AI-BATCH-CREATE-V1 + EF-AI-INTAKE-METRICS-V1
 * 
 * Features:
 * - Multi-project support with accordion
 * - Project selection with checkboxes
 * - Batch creation with partial success handling
 * - Split layout: Raw email (left) | Structured drafts (right)
 * - Editable fields with validation per project
 * - Entity matching with dropdowns
 * - **METRICS**: Edit tracking, unmatched entity tracking, analytics logging
 * 
 * CRITICAL: AI output is a DRAFT only. Backend validates and creates.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Bot, 
  AlertTriangle, 
  CheckCircle2, 
  Edit2, 
  Plus, 
  Trash2, 
  Loader2,
  Sparkles,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
  Users,
  Calendar,
  CheckSquare,
  Square,
  ExternalLink,
  XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../ui/collapsible';
import { toast } from 'sonner';
import api from '../../lib/api';

// Confidence level styling
// EVENTSFLO-PROJECT-INTAKE-SHIFT-LOCATION-MODEL-AND-HARD-WARNING-COLOURS-V1:
// Hard warning palette — yellow text on red for required/error states,
// no soft pastels. Deeply opaque, ring outlines, bold weight.
const getConfidenceStyle = (confidence) => {
  if (confidence >= 0.8) return 'bg-emerald-700 text-white border-emerald-300 font-bold ring-2 ring-emerald-400/50 shadow-sm';
  if (confidence >= 0.5) return 'bg-amber-600 text-black border-yellow-300 font-bold ring-2 ring-yellow-200/70 shadow-sm';
  return 'bg-red-700 text-yellow-300 border-yellow-300 font-bold ring-2 ring-yellow-200/70 shadow-sm';
};

// Project readiness status
const getProjectStatus = (pv, hasClient, hasVenue) => {
  if (!pv?.can_create) return { status: 'error', label: 'Needs Attention', color: 'text-red-500' };
  if (!hasClient || !hasVenue) return { status: 'warning', label: 'Missing Data', color: 'text-amber-500' };
  return { status: 'ready', label: 'Ready', color: 'text-emerald-500' };
};

/**
 * EF-AI-INTAKE-METRICS-V1: Build initial edit tracking state from AI draft.
 * Captures original_value and was_matched/was_unmatched flags for each field.
 */
const buildInitialEditTracking = (projects) => {
  return projects.map((project, idx) => ({
    projectIndex: idx,
    // Project-level fields
    project_name: {
      original_value: project.project?.name || '',
      final_value: project.project?.name || '',
      changed: false,
      was_matched: true, // AI always suggests a name
      was_unmatched: false,
    },
    client: {
      original_value: project.matches?.client_id || null,
      final_value: project.matches?.client_id || null,
      changed: false,
      was_matched: !!project.matches?.client_id,
      was_unmatched: !project.matches?.client_id,
    },
    location: {
      original_value: project.matches?.location_id || null,
      final_value: project.matches?.location_id || null,
      changed: false,
      was_matched: !!project.matches?.location_id,
      was_unmatched: !project.matches?.location_id,
    },
    // Shift-level aggregated tracking
    role: {
      original_count: project.shifts?.length || 0,
      edit_count: 0,
      changed: false,
      was_matched: project.shifts?.every(s => 
        project.matches?.role_matches?.some(rm => rm.input === s.role && rm.matched_id)
      ) ?? false,
      was_unmatched: project.shifts?.some(s => 
        !project.matches?.role_matches?.some(rm => rm.input === s.role && rm.matched_id)
      ) ?? true,
    },
    date: {
      original_value: project.project?.start_date || '',
      final_value: project.project?.start_date || '',
      changed: false,
      was_matched: true,
      was_unmatched: false,
    },
    time: {
      original_count: project.shifts?.length || 0,
      edit_count: 0,
      changed: false,
      was_matched: true,
      was_unmatched: false,
    },
  }));
};

export default function AIInterpreterPanel({ 
  open, 
  onClose, 
  emailText, 
  emailSubject,
  // EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1: optional sub-section of the email
  // body that the user highlighted. When non-empty, parser uses this and
  // shows a banner. When empty, parser falls back to the full emailText.
  selectedText = '',
  sourceMessageId, // For traceability
  onProjectsCreated // Callback after successful creation
}) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [validation, setValidation] = useState(null);
  const [editedProjects, setEditedProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set([0]));
  const [clients, setClients] = useState([]);
  const [venues, setVenues] = useState([]);
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  const [creationResult, setCreationResult] = useState(null);

  // EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1: track whether the active parse run
  // used the user-highlighted selection or the full email body. This is the
  // single source of truth shown in the UI banner and used by `parseWithAI`.
  // 'selection' = selection used; 'full' = full body used.
  const [parseScope, setParseScope] = useState('full');
  const [activeParseText, setActiveParseText] = useState('');

  // EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1: dry-run rule preview state
  const [rulePreview, setRulePreview] = useState(null);
  const [rulePreviewLoading, setRulePreviewLoading] = useState(false);
  const [rulePreviewOpen, setRulePreviewOpen] = useState(false);

  // EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1
  // Rule overrides keyed by `${projectIdx}::${shiftIdx}::${rule_id}`.
  // Value = { action: 'accept' | 'ignore' | 'alternate', task_definition_id?: string }.
  const [ruleOverrides, setRuleOverrides] = useState({});
  // Catalogue of supplement candidates per supplement_group, lazy-loaded.
  const [supplementOptionsByGroup, setSupplementOptionsByGroup] = useState({});
  // Post-create lock — once a successful create lands, the Create button
  // is disabled until the user explicitly Re-parses or closes the dialog.
  const [createLocked, setCreateLocked] = useState(false);
  // Snapshot of editedProjects-indices at the moment Preview Rules fires.
  // The preview endpoint receives only the SELECTED projects (in selection
  // order), so its `project_index` = position in that array. We must map
  // back to the original editedProjects index when storing per-rule
  // overrides keyed by `${origProjectIdx}::${shiftIdx}::${rule_id}`.
  const [previewSelectionMap, setPreviewSelectionMap] = useState([]);

  const overrideKey = (projectIdx, shiftIdx, ruleId) =>
    `${projectIdx}::${shiftIdx}::${ruleId}`;

  const setOverride = (projectIdx, shiftIdx, ruleId, patch) => {
    setRuleOverrides(prev => ({
      ...prev,
      [overrideKey(projectIdx, shiftIdx, ruleId)]: {
        ...(prev[overrideKey(projectIdx, shiftIdx, ruleId)] || {}),
        ...patch,
      },
    }));
  };

  const ensureSupplementOptions = async (group) => {
    if (!group || supplementOptionsByGroup[group] !== undefined) return;
    try {
      const r = await api.get('/task-definitions', {
        params: { supplement_group: group, is_active: true, limit: 100, enabled_filter: "enabled" },
      });
      setSupplementOptionsByGroup(prev => ({
        ...prev,
        [group]: (r.data || []).map(t => ({ id: t.id, name: t.name })),
      }));
    } catch (err) {
      // Fall back to silent empty list — UI will show only Accept / Ignore.
      setSupplementOptionsByGroup(prev => ({ ...prev, [group]: [] }));
    }
  };

  // EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1:
  // Inline Create Client / Create Venue from the draft review screen so the
  // user is not blocked by "Required before creation: Select a client/venue"
  // when the interpreted entities don't exist yet.
  const [createClientFor, setCreateClientFor] = useState(null);    // { projectIdx, prefillName }
  const [createVenueFor, setCreateVenueFor] = useState(null);      // { projectIdx, prefillName, prefillAddress }
  const [creatingEntity, setCreatingEntity] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', contact_email: '', contact_phone: '' });
  const [newVenue, setNewVenue] = useState({ name: '', address: '', city: '', postcode: '', country: '' });

  // EVENTSFLO-INBOX-FLAGS-WARNING-CONTRAST-AND-VENUE-LOCATION-FIX-V1:
  // Venue → VenueLocation hierarchy. A Venue is the building (e.g. "Royal
  // Albert Hall") and a VenueLocation is a meeting point within that venue
  // (e.g. "Backstage gate B"). The AI Draft must let the user select a
  // VenueLocation under the chosen Venue, and create one inline when missing.
  const [venueLocationsByVenue, setVenueLocationsByVenue] = useState({}); // { [venue_id]: [VenueLocation, ...] }
  const [createVenueLocationFor, setCreateVenueLocationFor] = useState(null); // { projectIdx, venueId, venueName, prefillName }
  const [newVenueLocation, setNewVenueLocation] = useState({ name: '', address1: '', postcode: '', country: '' });
  
  // EF-AI-INTAKE-METRICS-V1: Enhanced edit tracking per project
  const [editTracking, setEditTracking] = useState([]);
  const parseStartTime = useRef(null);
  
  // EF-AI-INTAKE-METRICS-V1: Simple field edit counts (aggregated)
  const [editCounts, setEditCounts] = useState({
    project_name: 0,
    client: 0,
    location: 0,
    role: 0,
    date: 0,
    time: 0
  });

  /**
   * EF-AI-INTAKE-METRICS-V1: Track field edit with detailed metadata.
   */
  const trackEdit = useCallback((projectIndex, fieldType, newValue) => {
    // Update simple counts
    setEditCounts(prev => ({
      ...prev,
      [fieldType]: prev[fieldType] + 1
    }));
    
    // Update detailed tracking
    setEditTracking(prev => {
      const newTracking = [...prev];
      if (newTracking[projectIndex]) {
        const field = newTracking[projectIndex][fieldType];
        if (field) {
          field.final_value = newValue;
          field.changed = field.original_value !== newValue;
          if (fieldType === 'role' || fieldType === 'time') {
            field.edit_count = (field.edit_count || 0) + 1;
            field.changed = field.edit_count > 0;
          }
        }
      }
      return newTracking;
    });
  }, []);

  // Fetch reference data on mount
  useEffect(() => {
    if (open) {
      fetchReferenceData();
      setCreationResult(null);
      // EF-AI-INTAKE-METRICS-V1: Reset all edit tracking
      setEditCounts({
        project_name: 0,
        client: 0,
        location: 0,
        role: 0,
        date: 0,
        time: 0
      });
      setEditTracking([]);
      parseStartTime.current = Date.now();
    }
  }, [open]);

  const fetchReferenceData = async () => {
    try {
      const [clientsRes, venuesRes, tasksRes] = await Promise.all([
        api.get('/clients?limit=100'),
        api.get('/venues?limit=100'),
        api.get('/task-definitions?limit=100&enabled_filter=enabled')
      ]);
      setClients(clientsRes.data || []);
      setVenues(venuesRes.data || []);
      setTaskDefinitions(tasksRes.data || []);
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  };

  // EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1
  const openCreateClient = useCallback((projectIdx, prefillName) => {
    setNewClient({ name: prefillName || '', contact_email: '', contact_phone: '' });
    setCreateClientFor({ projectIdx, prefillName: prefillName || '' });
  }, []);

  const openCreateVenue = useCallback((projectIdx, prefillName, prefillAddress) => {
    setNewVenue({
      name: prefillName || prefillAddress || '',
      address: prefillAddress || prefillName || '',
      city: '',
      postcode: '',
      country: '',
    });
    setCreateVenueFor({ projectIdx, prefillName: prefillName || '', prefillAddress: prefillAddress || '' });
  }, []);

  const submitCreateClient = useCallback(async () => {
    const name = (newClient.name || '').trim();
    if (!name) {
      toast.error('Client name is required');
      return;
    }
    setCreatingEntity(true);
    try {
      const payload = { name };
      const email = (newClient.contact_email || '').trim();
      const phone = (newClient.contact_phone || '').trim();
      if (email) payload.contact_email = email;
      if (phone) payload.contact_phone = phone;
      const res = await api.post('/clients', payload);
      const created = res.data;
      if (!created?.id) throw new Error('No client returned');
      setClients((prev) => [...prev, created]);
      if (createClientFor) {
        updateProjectField(createClientFor.projectIdx, 'matches.client_id', created.id);
      }
      toast.success(`Client "${created.name}" created and selected`);
      setCreateClientFor(null);
    } catch (err) {
      console.error('Create client failed:', err);
      toast.error(err?.response?.data?.detail || err.message || 'Could not create client');
    } finally {
      setCreatingEntity(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newClient, createClientFor]);

  const submitCreateVenue = useCallback(async () => {
    const name = (newVenue.name || '').trim();
    const address = (newVenue.address || '').trim();
    if (!name) { toast.error('Venue name is required'); return; }
    if (!address) { toast.error('Venue address is required'); return; }
    setCreatingEntity(true);
    try {
      const payload = { name, address };
      const city = (newVenue.city || '').trim();
      const postcode = (newVenue.postcode || '').trim();
      const country = (newVenue.country || '').trim();
      if (city) payload.city = city;
      if (postcode) payload.postcode = postcode;
      if (country) payload.country = country;
      const res = await api.post('/venues', payload);
      const created = res.data;
      if (!created?.id) throw new Error('No venue returned');
      setVenues((prev) => [...prev, created]);
      if (createVenueFor) {
        updateProjectField(createVenueFor.projectIdx, 'matches.location_id', created.id);
      }
      toast.success(`Venue "${created.name}" created and selected`);
      setCreateVenueFor(null);
    } catch (err) {
      console.error('Create venue failed:', err);
      toast.error(err?.response?.data?.detail || err.message || 'Could not create venue');
    } finally {
      setCreatingEntity(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVenue, createVenueFor]);

  // EVENTSFLO-INBOX-FLAGS-WARNING-CONTRAST-AND-VENUE-LOCATION-FIX-V1
  // Fetch the list of VenueLocations for a given venue (cached).
  const ensureVenueLocations = useCallback(async (venueId) => {
    if (!venueId) return;
    if (venueLocationsByVenue[venueId]) return;
    try {
      const res = await api.get(`/venue-locations?venue_id=${venueId}&limit=200`);
      setVenueLocationsByVenue((prev) => ({ ...prev, [venueId]: res.data || [] }));
    } catch (err) {
      console.warn('[venue-locations] fetch failed:', err);
      setVenueLocationsByVenue((prev) => ({ ...prev, [venueId]: [] }));
    }
  }, [venueLocationsByVenue]);

  const openCreateVenueLocation = useCallback((projectIdx, venueId, venueName, prefillName) => {
    if (!venueId) {
      toast.error('Select or create a Venue first');
      return;
    }
    setNewVenueLocation({
      name: prefillName || '',
      address1: '',
      postcode: '',
      country: '',
    });
    setCreateVenueLocationFor({ projectIdx, venueId, venueName: venueName || '', prefillName: prefillName || '' });
  }, []);

  const submitCreateVenueLocation = useCallback(async () => {
    if (!createVenueLocationFor?.venueId) return;
    const name = (newVenueLocation.name || '').trim();
    if (!name) { toast.error('Location name is required'); return; }
    setCreatingEntity(true);
    try {
      const payload = { venue_id: createVenueLocationFor.venueId, name };
      const a1 = (newVenueLocation.address1 || '').trim();
      const pc = (newVenueLocation.postcode || '').trim();
      const co = (newVenueLocation.country || '').trim();
      if (a1) payload.address1 = a1;
      if (pc) payload.postcode = pc;
      if (co) payload.country = co;
      const res = await api.post('/venue-locations', payload);
      const created = res.data;
      if (!created?.id) throw new Error('No venue location returned');
      setVenueLocationsByVenue((prev) => ({
        ...prev,
        [createVenueLocationFor.venueId]: [
          ...(prev[createVenueLocationFor.venueId] || []),
          created,
        ],
      }));
      // Auto-select on the originating project
      updateProjectField(createVenueLocationFor.projectIdx, 'matches.venue_location_id', created.id);
      toast.success(`Location "${created.name}" created under ${createVenueLocationFor.venueName || 'venue'}`);
      setCreateVenueLocationFor(null);
    } catch (err) {
      console.error('Create venue location failed:', err);
      toast.error(err?.response?.data?.detail || err.message || 'Could not create location');
    } finally {
      setCreatingEntity(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVenueLocation, createVenueLocationFor]);

  // EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1
  // Parse email with AI when dialog opens. If the user passed a non-empty
  // `selectedText`, parse only that. Otherwise parse the full email body.
  useEffect(() => {
    if (!open) return;
    const sel = (selectedText || '').trim();
    if (sel) {
      parseWithAI(sel, 'selection');
    } else if (emailText) {
      parseWithAI(emailText, 'full');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, emailText, selectedText]);

  const parseWithAI = async (textOverride, scopeOverride) => {
    // Resolve the active text + scope. Defaults: prefer selection prop,
    // fall back to full email body.
    const sel = (selectedText || '').trim();
    const text = (typeof textOverride === 'string' && textOverride.length > 0)
      ? textOverride
      : (sel || emailText || '');
    const scope = scopeOverride
      ? scopeOverride
      : (typeof textOverride === 'string' && textOverride.length > 0
          ? (textOverride === sel ? 'selection' : 'full')
          : (sel ? 'selection' : 'full'));

    setLoading(true);
    setAiResult(null);
    setValidation(null);
    setEditedProjects([]);
    setSelectedProjects(new Set());
    setCreationResult(null);
    setParseScope(scope);
    setActiveParseText(text);
    // EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1:
    // Re-parsing always clears the post-create lock and any prior overrides.
    setCreateLocked(false);
    setRuleOverrides({});
    setPreviewSelectionMap([]);
    parseStartTime.current = Date.now();

    try {
      const response = await api.post('/ai/parse-email', {
        email_text: text,
        email_subject: emailSubject
      });

      if (response.data.success) {
        setAiResult(response.data);
        setValidation(response.data.validation);
        setEditedProjects(response.data.projects || []);
        // Expand all projects by default
        setExpandedProjects(new Set(response.data.projects?.map((_, i) => i) || [0]));
        // Pre-select valid projects
        const validIndices = new Set();
        response.data.validation?.project_validations?.forEach((pv, idx) => {
          if (pv.can_create) validIndices.add(idx);
        });
        setSelectedProjects(validIndices);
        
        // EF-AI-INTAKE-METRICS-V1: Initialize detailed edit tracking
        const projects = response.data.projects || [];
        setEditTracking(buildInitialEditTracking(projects));
        // Reset edit counts
        setEditCounts({
          project_name: 0,
          client: 0,
          location: 0,
          role: 0,
          date: 0,
          time: 0
        });
        
        const scopeLabel = scope === 'selection' ? 'selected text' : 'full email';
        toast.success(`AI parsed ${response.data.projects?.length || 0} project(s) from ${scopeLabel}`);
      } else {
        toast.error(response.data.error || 'AI parsing failed');
      }
    } catch (err) {
      console.error('AI parse error:', err);
      toast.error('Failed to parse email with AI');
    } finally {
      setLoading(false);
    }
  };

  // Toggle project expansion
  const toggleProject = (index) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle project selection
  const toggleProjectSelection = (index) => {
    const pv = validation?.project_validations?.[index];
    const project = editedProjects[index];
    const hasClient = project?.matches?.client_id;
    const hasVenue = project?.matches?.location_id;
    
    // Only allow selection if project is valid and has required IDs
    if (!pv?.can_create) {
      toast.error('Fix validation errors before selecting');
      return;
    }
    if (!hasClient) {
      toast.error('Select a client before selecting for creation');
      return;
    }
    if (!hasVenue) {
      toast.error('Select a venue before selecting for creation');
      return;
    }
    
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Select all valid projects
  const selectAllValid = () => {
    const validIndices = new Set();
    editedProjects.forEach((project, idx) => {
      const pv = validation?.project_validations?.[idx];
      const hasClient = project?.matches?.client_id;
      const hasVenue = project?.matches?.location_id;
      if (pv?.can_create && hasClient && hasVenue) {
        validIndices.add(idx);
      }
    });
    setSelectedProjects(validIndices);
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedProjects(new Set());
  };

  // Update project field with edit tracking
  const updateProjectField = (projectIndex, path, value) => {
    setEditedProjects(prev => {
      const newProjects = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = newProjects[projectIndex];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newProjects;
    });
    
    // EF-AI-INTAKE-METRICS-V1: Track edits based on path
    if (path === 'project.name') {
      trackEdit(projectIndex, 'project_name', value);
    } else if (path === 'matches.client_id') {
      trackEdit(projectIndex, 'client', value);
    } else if (path === 'matches.location_id') {
      trackEdit(projectIndex, 'location', value);
    } else if (path === 'project.start_date' || path === 'project.end_date') {
      trackEdit(projectIndex, 'date', value);
    }
  };

  // Update shift field with edit tracking
  const updateShiftField = (projectIndex, shiftIndex, field, value) => {
    setEditedProjects(prev => {
      const newProjects = JSON.parse(JSON.stringify(prev));
      newProjects[projectIndex].shifts[shiftIndex][field] = value;
      return newProjects;
    });
    
    // EF-AI-INTAKE-METRICS-V1: Track shift-level edits
    if (field === 'role') {
      trackEdit(projectIndex, 'role', value);
    } else if (field === 'start_time' || field === 'duration_hours') {
      trackEdit(projectIndex, 'time', value);
    } else if (field === 'date') {
      trackEdit(projectIndex, 'date', value);
    }
  };

  // Add new shift to project
  const addShift = (projectIndex) => {
    setEditedProjects(prev => {
      const newProjects = JSON.parse(JSON.stringify(prev));
      const project = newProjects[projectIndex];
      project.shifts.push({
        role: '',
        quantity: 1,
        date: project.project?.start_date || '',
        start_time: '09:00',
        duration_hours: 8,
        notes: null
      });
      return newProjects;
    });
  };

  // Remove shift from project
  const removeShift = (projectIndex, shiftIndex) => {
    setEditedProjects(prev => {
      const newProjects = JSON.parse(JSON.stringify(prev));
      newProjects[projectIndex].shifts = newProjects[projectIndex].shifts.filter((_, i) => i !== shiftIndex);
      return newProjects;
    });
  };

  // EVENTSFLO-AI-DRAFT-APPLY-TO-ALL-TASK-LOCATION-V1
  // Apply-to-all dialog state: { field: 'task'|'location', projectIdx,
  //   sourceShiftIdx, value: <new value>, valueLabel, targets: [{
  //   shiftIdx, currentLabel, currentEqual, selected }] }
  const [applyAllDialog, setApplyAllDialog] = useState(null);

  const _taskLabel = useCallback((roleValue) => {
    if (!roleValue) return '— empty —';
    return String(roleValue);
  }, []);
  const _locationLabel = useCallback((venueId, venueLocationId) => {
    if (!venueId && !venueLocationId) return '— empty —';
    const v = venues.find((vn) => vn.id === venueId);
    const vName = v ? v.name : 'Venue';
    const locList = venueId ? venueLocationsByVenue[venueId] || [] : [];
    const loc = locList.find((l) => l.id === venueLocationId);
    if (loc) return `${vName} → ${loc.name}`;
    if (venueLocationId) return `${vName} → (location)`;
    return vName;
  }, [venues, venueLocationsByVenue]);

  const openApplyAll = useCallback((field, projectIdx, sourceShiftIdx) => {
    const project = editedProjects[projectIdx];
    if (!project || !project.shifts) return;
    const source = project.shifts[sourceShiftIdx];
    if (!source) return;

    if (field === 'task') {
      const sourceVal = source.role || '';
      if (!sourceVal.trim()) {
        toast.error('Set a Task value on this shift before applying to all.');
        return;
      }
      const targets = project.shifts.map((s, i) => {
        if (i === sourceShiftIdx) return null;
        const currentVal = s.role || '';
        const equal = currentVal === sourceVal;
        return {
          shiftIdx: i,
          currentLabel: _taskLabel(currentVal),
          currentEqual: equal,
          selected: !equal,
        };
      }).filter(Boolean);
      setApplyAllDialog({
        field: 'task',
        projectIdx,
        sourceShiftIdx,
        value: sourceVal,
        valueLabel: _taskLabel(sourceVal),
        targets,
      });
    } else if (field === 'location') {
      const sourceVenueId = source.venue_id || null;
      const sourceLocId = source.venue_location_id || null;
      if (!sourceVenueId && !sourceLocId) {
        toast.error('Set a Venue/Location on this shift before applying to all.');
        return;
      }
      // Make sure target venue locations are loaded for label rendering.
      if (sourceVenueId) ensureVenueLocations(sourceVenueId);
      const targets = project.shifts.map((s, i) => {
        if (i === sourceShiftIdx) return null;
        const equal =
          (s.venue_id || null) === sourceVenueId &&
          (s.venue_location_id || null) === sourceLocId;
        return {
          shiftIdx: i,
          currentLabel: _locationLabel(s.venue_id, s.venue_location_id),
          currentEqual: equal,
          selected: !equal,
        };
      }).filter(Boolean);
      setApplyAllDialog({
        field: 'location',
        projectIdx,
        sourceShiftIdx,
        value: { venue_id: sourceVenueId, venue_location_id: sourceLocId },
        valueLabel: _locationLabel(sourceVenueId, sourceLocId),
        targets,
      });
    }
  }, [editedProjects, _taskLabel, _locationLabel, ensureVenueLocations]);

  const toggleApplyTarget = useCallback((shiftIdx) => {
    setApplyAllDialog((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        targets: prev.targets.map((t) =>
          t.shiftIdx === shiftIdx && !t.currentEqual
            ? { ...t, selected: !t.selected }
            : t
        ),
      };
    });
  }, []);

  const confirmApplyAll = useCallback(() => {
    if (!applyAllDialog) return;
    const { field, projectIdx, value, targets } = applyAllDialog;
    const toApply = targets.filter((t) => t.selected && !t.currentEqual);
    if (toApply.length === 0) {
      toast('No shifts selected — nothing to apply.');
      return;
    }
    setEditedProjects((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const proj = next[projectIdx];
      if (!proj || !proj.shifts) return prev;
      toApply.forEach(({ shiftIdx }) => {
        const s = proj.shifts[shiftIdx];
        if (!s) return;
        if (field === 'task') {
          s.role = value;
        } else if (field === 'location') {
          s.venue_id = value.venue_id;
          s.venue_location_id = value.venue_location_id;
        }
      });
      return next;
    });
    toast.success(
      `Applied ${field === 'task' ? 'task' : 'location'} to ${toApply.length} shift${
        toApply.length === 1 ? '' : 's'
      }.`
    );
    setApplyAllDialog(null);
  }, [applyAllDialog]);

  // Re-validate all projects
  const revalidate = async () => {
    try {
      const response = await api.post('/ai/validate-draft', {
        projects: editedProjects,
        global_warnings: []
      });
      setValidation(response.data.validation);
      
      // Update selections based on new validation
      const validIndices = new Set();
      response.data.validation?.project_validations?.forEach((pv, idx) => {
        const project = editedProjects[idx];
        const hasClient = project?.matches?.client_id;
        const hasVenue = project?.matches?.location_id;
        if (pv.can_create && hasClient && hasVenue && selectedProjects.has(idx)) {
          validIndices.add(idx);
        }
      });
      setSelectedProjects(validIndices);
      
      if (response.data.validation.can_create_all) {
        toast.success('All projects are valid');
      } else if (response.data.validation.can_create_any) {
        toast.warning(`${response.data.validation.valid_projects}/${response.data.validation.total_projects} projects are valid`);
      } else {
        toast.error('No valid projects to create');
      }
    } catch (err) {
      toast.error('Validation failed');
    }
  };

  /**
   * EF-AI-INTAKE-METRICS-V1: Calculate unmatched entity counts from edited projects.
   */
  const calculateUnmatchedEntities = useCallback(() => {
    let clients = 0;
    let locations = 0;
    let roles = 0;
    
    editedProjects.forEach((project) => {
      if (!project.matches?.client_id) clients++;
      if (!project.matches?.location_id) locations++;
      
      // Count unmatched roles per shift
      project.shifts?.forEach(shift => {
        const roleMatch = project.matches?.role_matches?.find(rm => rm.input === shift.role);
        if (!roleMatch?.matched_id) roles++;
      });
    });
    
    return { clients, locations, roles };
  }, [editedProjects]);

  /**
   * EF-AI-INTAKE-METRICS-V1: Build edit details payload for analytics.
   */
  const buildEditDetailsPayload = useCallback(() => {
    const editDetails = {};
    const selectedIndices = Array.from(selectedProjects);
    
    selectedIndices.forEach(idx => {
      const tracking = editTracking[idx];
      if (!tracking) return;
      
      // Aggregate changed fields
      ['client', 'location', 'role', 'date', 'time'].forEach(field => {
        if (tracking[field]?.changed) {
          if (!editDetails[field]) {
            editDetails[field] = {
              changed: true,
              was_matched: tracking[field].was_matched,
              was_unmatched: tracking[field].was_unmatched,
            };
          }
        }
      });
    });
    
    return editDetails;
  }, [selectedProjects, editTracking]);

  // EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1: dry-run rule preview
  const handlePreviewRules = async () => {
    if (selectedProjects.size === 0) {
      toast.error('Select at least one project to preview rules');
      return;
    }
    setRulePreviewLoading(true);
    try {
      const selectionMap = Array.from(selectedProjects);
      const projectsToPreview = selectionMap.map(idx => editedProjects[idx]);
      // Snapshot mapping so override keys remain stable even if the user
      // changes selection between Preview and Create.
      setPreviewSelectionMap(selectionMap);
      const response = await api.post('/projects/preview-ai-draft-rules', {
        projects: projectsToPreview,
        source_message_id: sourceMessageId,
      });
      if (response.data?.success) {
        setRulePreview(response.data);
        setRulePreviewOpen(true);
        // EVENTSFLO-RULE-PREVIEW-UI-CORRECTION-V1: eagerly load alternates
        // for EVERY rule's supplement_group so each rule card knows whether
        // Alternate is available (and can render a populated dropdown).
        const groups = new Set();
        (response.data.projects || []).forEach(pr => {
          (pr.shifts || []).forEach(sh => {
            (sh.rules || []).forEach(r => {
              if (r.supplement_group) groups.add(r.supplement_group);
            });
          });
        });
        groups.forEach(g => ensureSupplementOptions(g));
      } else {
        toast.error('Could not preview rules');
      }
    } catch (err) {
      console.error('[AI_RULES_PREVIEW] failed:', err);
      toast.error('Could not preview rules: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setRulePreviewLoading(false);
    }
  };

  // Create selected projects
  const handleCreateProjects = async () => {
    if (selectedProjects.size === 0) {
      toast.error('No projects selected');
      return;
    }
    if (createLocked) {
      // EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1:
      // Hard guard against a second submit on the same parsed draft.
      toast.error('Projects already created from this draft. Re-parse to start over.');
      return;
    }
    setCreating(true);
    const processingTimeMs = parseStartTime.current ? Date.now() - parseStartTime.current : 0;
    
    try {
      // EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1
      // Get selected projects AND merge per-shift rule_overrides from state.
      // Override key format: `${origProjectIdx}::${shiftIdx}::${rule_id}`.
      // Payload per shift: rule_overrides: [{rule_id, action, task_definition_id?}]
      // Only `ignore` and `alternate` are sent — `accept` is the implicit default.
      const projectsToCreate = Array.from(selectedProjects).map(idx => {
        const proj = JSON.parse(JSON.stringify(editedProjects[idx]));
        proj.shifts = (proj.shifts || []).map((shift, sIdx) => {
          const overrides = [];
          Object.entries(ruleOverrides).forEach(([k, v]) => {
            const parts = k.split('::');
            if (parts.length !== 3) return;
            const [pi, si, rid] = parts;
            if (Number(pi) !== idx || Number(si) !== sIdx) return;
            const action = (v?.action || '').toLowerCase();
            if (action !== 'ignore' && action !== 'alternate') return;
            const entry = { rule_id: rid, action };
            if (action === 'alternate' && v?.task_definition_id) {
              entry.task_definition_id = v.task_definition_id;
            }
            overrides.push(entry);
          });
          return overrides.length ? { ...shift, rule_overrides: overrides } : shift;
        });
        return proj;
      });
      
      // EF-AI-INTAKE-METRICS-V1: Calculate metrics BEFORE creation
      const unmatchedEntities = calculateUnmatchedEntities();
      const editDetails = buildEditDetailsPayload();
      
      // Count validation errors from current validation state
      const validationErrors = validation?.project_validations?.reduce((sum, pv) => 
        sum + (pv.errors?.length || 0), 0
      ) || 0;
      
      // EF-AI-INTAKE-METRICS-V1: Send analytics (non-blocking)
      api.post('/ai/log-intake', {
        parser_type: 'ai_draft',
        email_id: sourceMessageId || emailSubject?.substring(0, 50) || null,
        projects_detected: editedProjects.length,
        projects_selected: selectedProjects.size,
        fields_edited: editCounts,
        edit_details: editDetails,
        unmatched_clients: unmatchedEntities.clients,
        unmatched_locations: unmatchedEntities.locations,
        unmatched_roles: unmatchedEntities.roles,
        validation_errors: validationErrors,
        processing_time_ms: processingTimeMs,
      }).catch(err => {
        // Non-blocking - don't fail creation if analytics fails
        console.warn('[AI_METRICS] Failed to log intake:', err);
      });
      
      // Create projects via batch endpoint
      const response = await api.post('/projects/create-from-ai-draft', {
        projects: projectsToCreate,
        source_message_id: sourceMessageId
      });
      
      const result = response.data;
      setCreationResult(result);
      
      if (result.success && result.summary.created > 0) {
        toast.success(`Created ${result.summary.created} project(s)${result.summary.failed > 0 ? `, ${result.summary.failed} failed` : ''}`);
        // EVENTSFLO-AI-OOT-REUSE-MANUAL-LOGIC-V1
        // Tell the user to populate distance/time/toll on any OOT shifts.
        // Backend has auto-created the 3 travel line items at qty=0 via
        // ensure_oot_travel_line_items; no logic is duplicated here.
        try {
          const ootCreated = (projectsToCreate || []).reduce((acc, p) => {
            return acc + (p.shifts || []).filter(s => s && s.user_confirmed_oot === true).length;
          }, 0);
          if (ootCreated > 0) {
            toast.message(
              `${ootCreated} OOT shift(s) created with placeholder travel values (qty=0). ` +
              `Open each shift and use Travel Advisor or edit manually to populate ` +
              `distance, travel time and toll.`,
              { duration: 9000 }
            );
          }
        } catch (_e) {
          /* never block creation on a UX hint */
        }
        // EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1:
        // Lock ALL further submission attempts on this parsed draft. The
        // user must explicitly Re-parse (which clears the lock) or close
        // the dialog to create more projects.
        setCreateLocked(true);
        
        // Remove successfully created projects from selection
        const createdIndices = new Set(result.created_projects.map(p => p.index));
        const remainingProjects = editedProjects.filter((_, idx) => 
          !Array.from(selectedProjects).some(selIdx => 
            createdIndices.has(Array.from(selectedProjects).indexOf(selIdx))
          )
        );
        
        // If all selected were created, close dialog
        if (result.summary.failed === 0) {
          if (onProjectsCreated) {
            onProjectsCreated(result.created_projects);
          }
          // Keep dialog open to show result
        }
      } else {
        toast.error('Failed to create projects');
      }
    } catch (err) {
      console.error('Create projects error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create projects');
    } finally {
      setCreating(false);
    }
  };

  // Get project validation
  const getProjectValidation = (index) => {
    return validation?.project_validations?.[index];
  };

  // EVENTSFLO-AI-DRAFT-TBD-VENUE-LOCATION-REVIEW-BADGE-V1:
  // Mirror of the backend `is_placeholder_location_text` helper. Used to
  // detect when the project will fall back to the tenant TBD Venue / TBD
  // Meeting Point on Create — so we can show an amber banner BEFORE the
  // admin clicks Create instead of only after.
  const PLACEHOLDER_LOCATION_TEXTS = new Set([
    "", "tbd", "tbc", "tba",
    "to be confirmed", "to be advised", "to be determined", "to be announced",
    "n/a", "na", "unknown",
    "onsite", "on-site", "on site",
    "venue tbd", "venue tbc", "location tbd", "location tbc",
  ]);
  const isPlaceholderLocationText = (v) => {
    if (v === null || v === undefined) return true;
    const norm = String(v).trim().toLowerCase();
    return norm === "" || PLACEHOLDER_LOCATION_TEXTS.has(norm);
  };
  const getTbdState = (project) => {
    const venueIdSelected = !!project?.matches?.location_id;
    const rawVenueText = project?.location?.raw || "";
    const rawMeetingText = project?.location?.meeting_point || "";
    const venueWillUseTbd = !venueIdSelected && isPlaceholderLocationText(rawVenueText);
    const meetingWillUseTbd =
      !project?.matches?.venue_location_id
      && isPlaceholderLocationText(rawMeetingText);
    return {
      venueWillUseTbd,
      meetingWillUseTbd,
      venueRawWasPlaceholder: !venueIdSelected && !!rawVenueText && isPlaceholderLocationText(rawVenueText),
      meetingRawWasPlaceholder: !!rawMeetingText && isPlaceholderLocationText(rawMeetingText),
      rawVenueText,
      rawMeetingText,
    };
  };

  // Check if project is ready for creation
  const isProjectReady = (index) => {
    const pv = getProjectValidation(index);
    const project = editedProjects[index];
    // EVENTSFLO-AI-DRAFT-TBD-VENUE-LOCATION-REVIEW-BADGE-V1:
    // Backend will accept missing venue and route to TBD Venue placeholder
    // (per EVENTSFLO-AI-DRAFT-TBD-VENUE-LOCATION-FALLBACK-V1). Don't block
    // Create in the UI — only client_id is still mandatory.
    return pv?.can_create && project?.matches?.client_id;
  };

  // Count total shifts across all projects
  const totalShifts = editedProjects.reduce((sum, p) => sum + (p.shifts?.length || 0), 0);
  const selectedCount = selectedProjects.size;
  const readyCount = editedProjects.filter((_, idx) => isProjectReady(idx)).length;
  // EVENTSFLO-RULE-PREVIEW-MINIMAL-CONTRAST-AND-DEFAULT-FIX-V1
  // Block create while any rule is in 'alternate' state without a chosen task.
  const hasUnpickedAlternate = Object.values(ruleOverrides).some(
    (v) => v && v.action === 'alternate' && !v.task_definition_id
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-secondary text-xl uppercase tracking-wide">
            <Bot className="w-5 h-5 text-primary" />
            AI Email Interpreter
            <Badge variant="outline" className="ml-2 text-xs">
              <Sparkles className="w-3 h-3 mr-1" />
              V2 Multi-Project
            </Badge>
          </DialogTitle>
          <DialogDescription>
            AI-powered email parsing with multi-project support. Select valid projects to create.
          </DialogDescription>
        </DialogHeader>

        {/* Creation Result Banner */}
        {creationResult && (
          <div className={`px-4 py-3 rounded-lg mb-2 ${
            creationResult.summary.failed === 0 
              ? 'bg-emerald-500/10 border border-emerald-500/30' 
              : 'bg-amber-600 border-2 border-yellow-300 text-black ring-2 ring-yellow-200/70 shadow-md'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {creationResult.summary.failed === 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                )}
                <span className="font-medium">
                  Created {creationResult.summary.created} project(s)
                  {creationResult.summary.failed > 0 && `, ${creationResult.summary.failed} failed`}
                </span>
              </div>
              <div className="flex gap-2">
                {creationResult.created_projects?.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      onClose();
                      // Navigate to projects
                      window.location.href = '/projects';
                    }}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    View Projects
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setCreationResult(null)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {creationResult.created_projects?.length > 0 && (
              <div className="mt-2 text-sm text-muted-foreground">
                Created: {creationResult.created_projects.map(p => p.project_name).join(', ')}
              </div>
            )}
            {creationResult.failed_projects?.length > 0 && (
              <div className="mt-2 text-sm text-yellow-200 font-bold bg-red-700 ring-2 ring-yellow-300 px-2 py-1.5 rounded">
                Failed: {creationResult.failed_projects.map(p => `${p.project_name || 'Unnamed'} (${p.errors.join(', ')})`).join('; ')}
              </div>
            )}
            {/* EVENTSFLO-AI-DRAFT-MUST-TRIGGER-DETERMINISTIC-RULES-V1 +
                EVENTSFLO-AI-RULES-OUTPUT-CLEANUP-V1:
                Surface deterministic rule outcomes (Crew Chief, Meal Allowance,
                etc.) and any rule/shift warnings returned by the backend, so
                the user sees the same rule application that established
                non-AI flows produce — and any failures are not hidden.
                Display-only cleanup: dedupe per (project, shift, rule),
                group by project then shift, preserve first-seen ordering. */}
            {(() => {
              const created = creationResult.created_projects || [];
              const allRuleWarnings = created.flatMap(p => (p.rule_warnings || []).map(w => `${p.project_name}: ${w}`));
              const allShiftWarnings = created.flatMap(p => (p.shift_warnings || []).map(w => `${p.project_name}: ${w}`));

              // Parse "[Shift Label] Rule Name" -> { shift, rule }.
              // Fall back to shift = '' (general) if no label is present.
              const parseEntry = (raw) => {
                if (typeof raw !== 'string') return { shift: '', rule: String(raw) };
                const m = raw.match(/^\s*\[([^\]]+)\]\s*(.*)$/);
                if (m) return { shift: m[1].trim(), rule: m[2].trim() };
                return { shift: '', rule: raw.trim() };
              };

              // Build [{ projectName, shifts: [{ shift, rules: [ruleName,...] }] }]
              // with first-seen-stable ordering and (project, shift, rule) dedup.
              const grouped = [];
              const projectIdx = new Map();
              created.forEach((p) => {
                const projectName = p.project_name || 'Project';
                const entries = (p.applied_rules || []).map(parseEntry);
                if (entries.length === 0) return;

                if (!projectIdx.has(projectName)) {
                  projectIdx.set(projectName, grouped.length);
                  grouped.push({ projectName, shifts: [], _shiftIdx: new Map(), _seen: new Set() });
                }
                const bucket = grouped[projectIdx.get(projectName)];

                entries.forEach(({ shift, rule }) => {
                  if (!rule) return;
                  const dedupKey = `${shift}::${rule}`;
                  if (bucket._seen.has(dedupKey)) return;
                  bucket._seen.add(dedupKey);

                  if (!bucket._shiftIdx.has(shift)) {
                    bucket._shiftIdx.set(shift, bucket.shifts.length);
                    bucket.shifts.push({ shift, rules: [] });
                  }
                  bucket.shifts[bucket._shiftIdx.get(shift)].rules.push(rule);
                });
              });

              const totalRules = grouped.reduce(
                (sum, g) => sum + g.shifts.reduce((s, sh) => s + sh.rules.length, 0),
                0
              );

              return (
                <>
                  {totalRules > 0 && (
                    <div className="mt-2 text-xs bg-emerald-700 text-white font-semibold ring-2 ring-emerald-300 px-2 py-1.5 rounded" data-testid="ai-creation-applied-rules">
                      <div className="uppercase tracking-wide font-bold mb-1 sticky top-0 bg-emerald-700 z-10 -mx-2 px-2 pb-1 border-b border-emerald-500/40">
                        Rules applied ({totalRules})
                      </div>
                      {/* EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1: scrollable
                          report — long rule lists must not push the create result
                          banner off-screen or make the dialog unusable. */}
                      <div
                        className="space-y-1.5 max-h-56 overflow-y-auto pr-1"
                        data-testid="ai-creation-applied-rules-scroll"
                      >
                        {grouped.map((g, gi) => (
                          <div key={`grp-${gi}`} data-testid={`applied-rules-project-${gi}`}>
                            {grouped.length > 1 && (
                              <div className="font-bold uppercase tracking-wide text-emerald-100 text-[11px]">
                                {g.projectName}
                              </div>
                            )}
                            {g.shifts.map((sh, si) => (
                              <div key={`grp-${gi}-sh-${si}`} className="ml-1">
                                <div className="font-semibold text-emerald-100">
                                  {sh.shift ? `[${sh.shift}]` : '(general)'}
                                </div>
                                <ul className="ml-3 space-y-0.5">
                                  {sh.rules.map((r, ri) => (
                                    <li key={`grp-${gi}-sh-${si}-r-${ri}`}>• {r}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(allRuleWarnings.length > 0 || allShiftWarnings.length > 0) && (
                    <div className="mt-2 text-xs bg-red-700 text-yellow-200 font-bold ring-2 ring-yellow-300 px-2 py-1.5 rounded" data-testid="ai-creation-rule-warnings">
                      <div className="uppercase tracking-wide font-bold mb-0.5 sticky top-0 bg-red-700 z-10 -mx-2 px-2 pb-1 border-b border-yellow-300/40">Action required — backend issues:</div>
                      <ul className="space-y-0.5 max-h-48 overflow-y-auto pr-1" data-testid="ai-creation-rule-warnings-scroll">
                        {allShiftWarnings.map((w, i) => <li key={`sw-${i}`}>• {w}</li>)}
                        {allRuleWarnings.map((w, i) => <li key={`rw-${i}`}>• {w}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Analyzing email with AI...</p>
              <p className="text-xs text-muted-foreground">Detecting multiple projects and shifts</p>
            </div>
          </div>
        ) : editedProjects.length > 0 ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Summary Banner */}
            <div className="px-4 py-2 rounded-lg mb-4 flex items-center justify-between bg-surface-elevated border border-white/10">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">
                    <strong>{editedProjects.length}</strong> Project{editedProjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">
                    <strong>{totalShifts}</strong> Shift{totalShifts !== 1 ? 's' : ''}
                  </span>
                </div>
                <Badge 
                  variant="outline" 
                  className={readyCount === editedProjects.length 
                    ? 'border-emerald-200 bg-emerald-700 text-white font-bold ring-2 ring-emerald-400/50 shadow-sm' 
                    : readyCount > 0 
                      ? 'border-yellow-300 bg-amber-600 text-black font-bold ring-2 ring-yellow-200/70 shadow-sm'
                      : 'border-yellow-300 bg-red-700 text-yellow-300 font-bold ring-2 ring-yellow-200/70 shadow-sm'
                  }
                >
                  {readyCount}/{editedProjects.length} Ready
                </Badge>
                {selectedCount > 0 && (
                  <Badge variant="default" className="bg-primary">
                    {selectedCount} Selected
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllValid}>
                  <CheckSquare className="w-3 h-3 mr-1" />
                  Select All Ready
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  Clear
                </Button>
                <Button variant="ghost" size="sm" onClick={revalidate}>
                  Re-validate
                </Button>
              </div>
            </div>

            {/* Global Warnings */}
            {validation?.global_warnings?.length > 0 && (
              <div className="mb-4 p-3 bg-amber-500 border-2 border-yellow-300 text-black ring-2 ring-yellow-200/70 shadow-md rounded-lg">
                <div className="flex items-center gap-2 text-black text-sm font-bold mb-2 uppercase tracking-wide">
                  <AlertCircle className="w-4 h-4" />
                  Global Warnings
                </div>
                <ul className="space-y-1 text-sm text-black font-semibold">
                  {validation.global_warnings.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Split Layout */}
            <div className="flex-1 overflow-hidden grid grid-cols-5 gap-4">
              {/* Left: Raw Email (2 columns) */}
              <div className="col-span-2 overflow-y-auto border border-white/10 rounded-lg p-4 bg-surface-elevated">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                  <Info className="w-3 h-3" />
                  Original Email
                </h3>
                {/* EVENTSFLO-AI-PARSER-SELECTION-SCOPE-V1: Scope indicator banner */}
                {(() => {
                  const sel = (selectedText || '').trim();
                  const usingSelection = parseScope === 'selection' && !!sel;
                  if (usingSelection) {
                    return (
                      <div
                        className="mb-3 p-2 rounded bg-purple-700 text-white ring-2 ring-purple-300 text-xs flex items-center justify-between gap-2"
                        data-testid="ai-parser-scope-banner-selection"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Sparkles className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-bold uppercase tracking-wide">
                            Parsing selected text only
                          </span>
                          <span className="text-purple-100 truncate">
                            ({sel.length.toLocaleString()} chars)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] bg-white text-purple-800 hover:bg-purple-50 border-white shrink-0"
                          onClick={() => parseWithAI(emailText, 'full')}
                          data-testid="ai-parser-use-full-btn"
                          title="Re-parse the full email body instead"
                        >
                          Use full email
                        </Button>
                      </div>
                    );
                  }
                  if (sel) {
                    // Selection prop exists but full body is currently parsed.
                    return (
                      <div
                        className="mb-3 p-2 rounded bg-zinc-800 text-zinc-100 ring-1 ring-zinc-600 text-xs flex items-center justify-between gap-2"
                        data-testid="ai-parser-scope-banner-full"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Info className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-semibold">Parsing full email body</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] shrink-0"
                          onClick={() => parseWithAI(sel, 'selection')}
                          data-testid="ai-parser-use-selection-btn"
                          title="Re-parse only the highlighted selection"
                        >
                          Use selection ({sel.length.toLocaleString()} chars)
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })()}
                {emailSubject && (
                  <div className="mb-3 pb-3 border-b border-white/10">
                    <span className="text-xs text-muted-foreground">Subject:</span>
                    <p className="font-medium text-sm">{emailSubject}</p>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm text-muted-foreground font-mono"
                     data-testid="ai-parser-active-text">
                  {activeParseText || emailText}
                </div>
              </div>

              {/* Right: Projects Accordion (3 columns) */}
              <div className="col-span-3 overflow-y-auto space-y-3">
                {editedProjects.map((project, projectIdx) => {
                  const pv = getProjectValidation(projectIdx);
                  const isExpanded = expandedProjects.has(projectIdx);
                  const isSelected = selectedProjects.has(projectIdx);
                  const confidence = project.matches?.confidence || 0.5;
                  const hasClient = !!project.matches?.client_id;
                  const hasVenue = !!project.matches?.location_id;
                  const projectStatus = getProjectStatus(pv, hasClient, hasVenue);
                  const isReady = isProjectReady(projectIdx);
                  
                  return (
                    <Collapsible 
                      key={projectIdx}
                      open={isExpanded}
                      onOpenChange={() => toggleProject(projectIdx)}
                      className={`border rounded-lg overflow-hidden ${
                        isSelected 
                          ? 'border-primary/50 bg-primary/5' 
                          : 'border-white/10'
                      }`}
                    >
                      <div 
                        className={`p-3 flex items-center justify-between ${
                          isExpanded ? 'border-b border-white/10' : ''
                        }`}
                      >
                        {/* Selection Checkbox */}
                        <div className="flex items-center gap-3">
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleProjectSelection(projectIdx);
                            }}
                            className={`cursor-pointer ${!isReady ? 'opacity-50' : ''}`}
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-primary" />
                            ) : (
                              <Square className={`w-5 h-5 ${isReady ? 'text-muted-foreground hover:text-primary' : 'text-muted-foreground/50'}`} />
                            )}
                          </div>
                          
                          <CollapsibleTrigger asChild>
                            <div 
                              className="flex items-center gap-2 cursor-pointer flex-1"
                              data-testid={`project-accordion-${projectIdx}`}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                              <div>
                                <div className="font-medium text-sm flex items-center gap-2">
                                  {project.project?.name || `Project ${projectIdx + 1}`}
                                  <Badge 
                                    variant="outline" 
                                    className={`text-[9px] ${getConfidenceStyle(confidence)}`}
                                  >
                                    {(confidence * 100).toFixed(0)}%
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                                  {project.project?.start_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {project.project.start_date}
                                    </span>
                                  )}
                                  {/* EVENTSFLO-PROJECT-INTAKE-SHIFT-LOCATION-MODEL-AND-HARD-WARNING-COLOURS-V1:
                                      Project-level Location removed — Venue is the project-level concept,
                                      and Location is per-shift (within Venue). */}
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {project.shifts?.length || 0} shift(s)
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`text-[9px] font-bold ${
                              projectStatus.status === 'ready' 
                                ? 'border-emerald-200 bg-emerald-700 text-white ring-1 ring-emerald-400/50'
                                : projectStatus.status === 'warning'
                                  ? 'border-yellow-300 bg-amber-600 text-black ring-2 ring-yellow-200/70 shadow-sm'
                                  : 'border-yellow-300 bg-red-700 text-yellow-300 ring-2 ring-yellow-200/70 shadow-sm'
                            }`}
                          >
                            {projectStatus.label}
                          </Badge>
                        </div>
                      </div>
                      
                      <CollapsibleContent>
                        <div className="p-4 space-y-4">
                          {/* Validation Warnings for this project */}
                          {pv?.warnings?.length > 0 && (
                            <div className="p-2 bg-amber-500 border-2 border-yellow-300 text-black rounded text-xs ring-1 ring-yellow-200/60 shadow-sm">
                              <div className="text-black font-bold mb-1 uppercase tracking-wide">Warnings:</div>
                              <ul className="text-black font-semibold space-y-0.5">
                                {pv.warnings.slice(0, 3).map((w, i) => (
                                  <li key={i}>• {w}</li>
                                ))}
                                {pv.warnings.length > 3 && (
                                  <li className="italic">...and {pv.warnings.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {/* Required Fields Alert */}
                          {(() => {
                            // EVENTSFLO-PROJECT-INTAKE-SHIFT-LOCATION-MODEL-AND-HARD-WARNING-COLOURS-V1
                            // Validate per-shift Venue + Location (within venue) + Description.
                            const projectVenueId = project.matches?.location_id || null;
                            const shiftsMissingVenue = (project.shifts || [])
                              .map((s, i) => ({ i, s }))
                              .filter(({ s }) => !(s.venue_id || projectVenueId));
                            const shiftsMissingLoc = (project.shifts || [])
                              .map((s, i) => ({ i, s }))
                              .filter(({ s }) => !s.venue_location_id);
                            const shiftsMissingDesc = (project.shifts || [])
                              .map((s, i) => ({ i, s }))
                              .filter(({ s }) => !((s.description || '').trim()));
                            // EVENTSFLO-AI-DRAFT-TBD-VENUE-LOCATION-REVIEW-BADGE-V1
                            const tbd = getTbdState(project);
                            const showTbdBanner = tbd.venueWillUseTbd || tbd.meetingWillUseTbd;
                            // Venue is NO LONGER a hard blocker — backend routes to TBD Venue.
                            // Red "Required before creation" only fires for client / shift-level issues.
                            const showAlert = (!hasClient
                              || shiftsMissingVenue.length > 0
                              || shiftsMissingLoc.length > 0
                              || shiftsMissingDesc.length > 0);
                            return (
                              <>
                                {showTbdBanner && (
                                  <div
                                    className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-400/60 text-amber-900 dark:text-amber-200 rounded text-xs"
                                    data-testid={`project-${projectIdx}-tbd-warning`}
                                  >
                                    <div className="font-semibold flex items-center gap-1 mb-1">
                                      <AlertCircle className="w-3.5 h-3.5" />
                                      Venue / location incomplete — this draft will use TBD Venue / TBD Meeting Point.
                                    </div>
                                    <ul className="space-y-0.5 ml-5 list-disc">
                                      {tbd.venueWillUseTbd && (
                                        <li>
                                          {tbd.venueRawWasPlaceholder
                                            ? <>Venue text <code className="font-mono">"{tbd.rawVenueText}"</code> treated as missing — will save as <strong>TBD Venue</strong>.</>
                                            : <>No venue provided — will save as <strong>TBD Venue</strong>.</>}
                                        </li>
                                      )}
                                      {tbd.meetingWillUseTbd && (
                                        <li>
                                          {tbd.meetingRawWasPlaceholder
                                            ? <>Meeting point <code className="font-mono">"{tbd.rawMeetingText}"</code> treated as missing — will save as <strong>TBD</strong>.</>
                                            : <>No meeting point provided — will save as <strong>TBD</strong>.</>}
                                        </li>
                                      )}
                                    </ul>
                                    <div className="mt-1 text-[11px]">
                                      You can pick a real venue below, or click <strong>Create Venue</strong> to add one inline. Otherwise, update the project / shift after creation before confirming with the client.
                                    </div>
                                  </div>
                                )}
                                {showAlert && (
                              <div className="p-2 bg-red-700 border-2 border-yellow-300 text-yellow-300 rounded text-xs ring-2 ring-yellow-300/70 shadow-md">
                                <div className="text-yellow-300 font-bold mb-1 flex items-center gap-1 uppercase tracking-wide text-[11px]">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  Required before creation:
                                </div>
                                <ul className="text-yellow-200 font-semibold space-y-0.5">
                                  {!hasClient && <li>• Select a client</li>}
                                  {shiftsMissingVenue.length > 0 && (
                                    <li>• {shiftsMissingVenue.length} shift(s) missing venue</li>
                                  )}
                                  {shiftsMissingLoc.length > 0 && (
                                    <li>• {shiftsMissingLoc.length} shift(s) missing location (within venue)</li>
                                  )}
                                  {shiftsMissingDesc.length > 0 && (
                                    <li>• {shiftsMissingDesc.length} shift(s) missing description</li>
                                  )}
                                </ul>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {!hasClient && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-[11px] bg-yellow-300 text-red-800 border-yellow-200 hover:bg-yellow-200 font-bold"
                                      onClick={() => openCreateClient(projectIdx, project.project?.client_name)}
                                      data-testid={`project-${projectIdx}-required-create-client-btn`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Create Client{project.project?.client_name ? ` "${project.project.client_name}"` : ''}
                                    </Button>
                                  )}
                                  {!hasVenue && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-[11px] bg-yellow-300 text-red-800 border-yellow-200 hover:bg-yellow-200 font-bold"
                                      onClick={() => openCreateVenue(projectIdx, project.location?.raw, project.location?.raw)}
                                      data-testid={`project-${projectIdx}-required-create-venue-btn`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Create Venue{project.location?.raw ? ` "${project.location.raw}"` : ''}
                                    </Button>
                                  )}
                                  {shiftsMissingVenue.length > 0 && projectVenueId && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-[11px] bg-yellow-300 text-red-800 border-yellow-200 hover:bg-yellow-200 font-bold"
                                      onClick={() => {
                                        shiftsMissingVenue.forEach(({ i }) => {
                                          updateShiftField(projectIdx, i, 'venue_id', projectVenueId);
                                        });
                                      }}
                                      data-testid={`project-${projectIdx}-fill-shift-venues-btn`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Default {shiftsMissingVenue.length} shift(s) to project venue
                                    </Button>
                                  )}
                                </div>
                              </div>
                                )}
                              </>
                            );
                          })()}
                          
                          {/* Project Details */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Project Name</Label>
                              <Input
                                value={project.project?.name || ''}
                                onChange={(e) => updateProjectField(projectIdx, 'project.name', e.target.value)}
                                className="mt-1 h-8 text-sm"
                                data-testid={`project-${projectIdx}-name`}
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs flex items-center gap-1">
                                Client
                                {!hasClient && (
                                  <Badge variant="outline" className="ml-1 text-[9px] text-yellow-300 bg-red-700 border-yellow-300 font-bold ring-2 ring-yellow-200/70 shadow-sm">
                                    Required
                                  </Badge>
                                )}
                              </Label>
                              <div className="flex items-center gap-1.5 mt-1">
                                <Select
                                  value={project.matches?.client_id || 'none'}
                                  onValueChange={(value) => updateProjectField(projectIdx, 'matches.client_id', value === 'none' ? null : value)}
                                >
                                  <SelectTrigger className={`h-8 text-sm flex-1 ${!hasClient ? 'border-red-500/50' : ''}`}>
                                    <SelectValue placeholder={project.project?.client_name || 'Select client...'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      <span className="text-muted-foreground">-- Select client --</span>
                                    </SelectItem>
                                    {clients.map(c => (
                                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {/* EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1 */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-xs whitespace-nowrap shrink-0"
                                  onClick={() => openCreateClient(projectIdx, project.project?.client_name)}
                                  data-testid={`project-${projectIdx}-create-client-btn`}
                                  title="Create a new client from this draft"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  New
                                </Button>
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs flex items-center gap-1">
                                Venue
                                {!hasVenue && (
                                  <Badge variant="outline" className="ml-1 text-[9px] text-yellow-300 bg-red-700 border-yellow-300 font-bold ring-2 ring-yellow-200/70 shadow-sm">
                                    Required
                                  </Badge>
                                )}
                              </Label>
                              <div className="flex items-center gap-1.5 mt-1">
                                <Select
                                  value={project.matches?.location_id || 'none'}
                                  onValueChange={(value) => updateProjectField(projectIdx, 'matches.location_id', value === 'none' ? null : value)}
                                >
                                  <SelectTrigger className={`h-8 text-sm flex-1 ${!hasVenue ? 'border-red-500/50' : ''}`}>
                                    <SelectValue placeholder={project.location?.raw || 'Select venue...'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      <span className="text-muted-foreground">-- Select venue --</span>
                                    </SelectItem>
                                    {venues.map(v => (
                                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {/* EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1 */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-xs whitespace-nowrap shrink-0"
                                  onClick={() => openCreateVenue(projectIdx, project.location?.raw, project.location?.raw)}
                                  data-testid={`project-${projectIdx}-create-venue-btn`}
                                  title="Create a new venue from this draft"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  New
                                </Button>
                              </div>
                              {/* EVENTSFLO-PROJECT-INTAKE-SHIFT-LOCATION-MODEL-AND-HARD-WARNING-COLOURS-V1:
                                  VenueLocation moved OUT of project level — see per-shift Venue/Location below. */}
                            </div>

                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Label className="text-xs">Start Date</Label>
                                <Input
                                  type="date"
                                  value={project.project?.start_date || ''}
                                  onChange={(e) => updateProjectField(projectIdx, 'project.start_date', e.target.value)}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                              <div className="flex-1">
                                <Label className="text-xs">End Date</Label>
                                <Input
                                  type="date"
                                  value={project.project?.end_date || ''}
                                  onChange={(e) => updateProjectField(projectIdx, 'project.end_date', e.target.value)}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* EVENTSFLO-AI-DRAFT-CONTACT-OVERRIDE-POSTCREATE-LOCK-V1
                              Project Contact Person — extracted from email sender block
                              (see ai_parser_service.py contact_name/email/phone). Rendered
                              always so the user can correct or add a contact manually. */}
                          {(() => {
                            const cn = project.project?.contact_name || '';
                            const ce = project.project?.contact_email || '';
                            const cp = project.project?.contact_phone || '';
                            const aiExtracted = !!(cn || ce || cp);
                            return (
                            <div
                              className="rounded-md border-2 border-cyan-300 bg-cyan-900 text-white p-3"
                              data-testid={`project-${projectIdx}-contact`}
                            >
                              <Label className="text-[10px] uppercase tracking-wider text-yellow-200 font-bold flex items-center gap-1.5 mb-2">
                                Project Contact Person
                                {aiExtracted && (
                                  <Badge className="bg-yellow-300 text-black border-black text-[9px] font-bold uppercase">
                                    AI-extracted
                                  </Badge>
                                )}
                              </Label>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-[10px] text-cyan-100">Name</Label>
                                  <Input
                                    value={cn}
                                    onChange={(e) => updateProjectField(projectIdx, 'project.contact_name', e.target.value)}
                                    className="mt-1 h-8 text-sm bg-cyan-950 border-cyan-300 text-white"
                                    placeholder="Contact name"
                                    data-testid={`project-${projectIdx}-contact-name`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-cyan-100">Email</Label>
                                  <Input
                                    type="email"
                                    value={ce}
                                    onChange={(e) => updateProjectField(projectIdx, 'project.contact_email', e.target.value)}
                                    className="mt-1 h-8 text-sm bg-cyan-950 border-cyan-300 text-white"
                                    placeholder="contact@example.com"
                                    data-testid={`project-${projectIdx}-contact-email`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-cyan-100">Phone</Label>
                                  <Input
                                    value={cp}
                                    onChange={(e) => updateProjectField(projectIdx, 'project.contact_phone', e.target.value)}
                                    className="mt-1 h-8 text-sm bg-cyan-950 border-cyan-300 text-white"
                                    placeholder="+44 ..."
                                    data-testid={`project-${projectIdx}-contact-phone`}
                                  />
                                </div>
                              </div>
                              <div className="text-[10px] text-cyan-100 mt-2 italic">
                                Contact details are saved into the project description until a first-class
                                Client Contact link is added.
                              </div>
                            </div>
                            );
                          })()}

                          {/* Shifts */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs uppercase tracking-wider">
                                Shifts ({project.shifts?.length || 0})
                              </Label>
                              <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => addShift(projectIdx)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            </div>
                            
                            <div className="space-y-2">
                              {project.shifts?.map((shift, shiftIdx) => {
                                const roleMatch = project.matches?.role_matches?.find(rm => rm.input === shift.role);
                                
                                return (
                                  <div 
                                    key={shiftIdx} 
                                    className="p-2 rounded border border-white/10 bg-background/50 space-y-2"
                                    data-testid={`project-${projectIdx}-shift-${shiftIdx}`}
                                  >
                                    <div className="grid grid-cols-6 gap-2 items-end">
                                    <div className="col-span-2">
                                      <div className="flex items-center justify-between gap-1">
                                        <Label className="text-[10px]">Task</Label>
                                        {project.shifts.length > 1 && (
                                          <button
                                            type="button"
                                            className="text-[9px] uppercase tracking-wide text-cyan-300 hover:text-cyan-100 hover:underline disabled:opacity-40 disabled:no-underline"
                                            disabled={!shift.role}
                                            onClick={() => openApplyAll('task', projectIdx, shiftIdx)}
                                            data-testid={`project-${projectIdx}-shift-${shiftIdx}-apply-task-all-btn`}
                                            title="Apply this task to all other shifts in this project"
                                          >
                                            Apply to all
                                          </button>
                                        )}
                                      </div>
                                      <Select
                                        value={roleMatch?.matched_id || 'custom'}
                                        onValueChange={(value) => {
                                          if (value !== 'custom') {
                                            const task = taskDefinitions.find(t => t.id === value);
                                            if (task) updateShiftField(projectIdx, shiftIdx, 'role', task.name);
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="h-7 text-xs">
                                          <SelectValue placeholder={shift.role || 'Select task...'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="custom">
                                            <span className="text-muted-foreground">{shift.role || 'Custom'}</span>
                                          </SelectItem>
                                          {taskDefinitions.map(t => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div>
                                      <Label className="text-[10px]">Qty</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={shift.quantity}
                                        onChange={(e) => updateShiftField(projectIdx, shiftIdx, 'quantity', parseInt(e.target.value) || 1)}
                                        className="h-7 text-xs"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-[10px]">Date</Label>
                                      <Input
                                        type="date"
                                        value={shift.date || ''}
                                        onChange={(e) => updateShiftField(projectIdx, shiftIdx, 'date', e.target.value)}
                                        className="h-7 text-xs"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-[10px]">Start</Label>
                                      <Input
                                        type="time"
                                        value={shift.start_time || ''}
                                        onChange={(e) => {
                                          updateShiftField(projectIdx, shiftIdx, 'start_time', e.target.value);
                                          // Clear placeholder warning once the user changes the value.
                                          const ws = (shift.warnings || []).filter(w => w.code !== 'MISSING_START_TIME_PLACEHOLDER');
                                          if (ws.length !== (shift.warnings || []).length) {
                                            updateShiftField(projectIdx, shiftIdx, 'warnings', ws);
                                          }
                                        }}
                                        className={
                                          'h-7 text-xs ' + (
                                            (shift.warnings || []).some(w => w.code === 'MISSING_START_TIME_PLACEHOLDER')
                                              ? 'border-2 border-amber-500 bg-amber-50 text-amber-900'
                                              : ''
                                          )
                                        }
                                        data-testid={`ai-shift-${projectIdx}-${shiftIdx}-start-time`}
                                      />
                                      {(shift.warnings || []).filter(w => w.code === 'MISSING_START_TIME_PLACEHOLDER').map((w, wi) => (
                                        <div
                                          key={`stw-${wi}`}
                                          className="mt-1 text-[10px] font-bold text-amber-800 bg-amber-100 border-l-2 border-amber-500 px-1.5 py-0.5 rounded"
                                          data-testid={`ai-shift-${projectIdx}-${shiftIdx}-start-time-warning`}
                                        >
                                          ⚠ {w.message}
                                        </div>
                                      ))}
                                    </div>
                                    <div className="flex gap-1">
                                      <div className="flex-1">
                                        <Label className="text-[10px]">Hours</Label>
                                        <Input
                                          type="number"
                                          min="0.5"
                                          step="0.5"
                                          value={shift.duration_hours || ''}
                                          onChange={(e) => updateShiftField(projectIdx, shiftIdx, 'duration_hours', parseFloat(e.target.value) || 0)}
                                          className="h-7 text-xs"
                                        />
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-400 self-end"
                                        onClick={() => removeShift(projectIdx, shiftIdx)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    </div>
                                    {/* EVENTSFLO-OOT-MANUAL-CONFIRMATION-RULE-V1
                                        Per-shift OOT toggle + AI suggestion notice.
                                        Toggle starts UNCHECKED on every parse. AI may suggest
                                        but never sets out_of_town. */}
                                    {(() => {
                                      const ootSugg = shift.oot_suggestion || {};
                                      const aiSuggested = !!ootSugg.suggested;
                                      const isChecked = !!shift.out_of_town;
                                      return (
                                        <div className="mt-2 mb-2 rounded border border-slate-200 bg-slate-50 p-2"
                                             data-testid={`ai-shift-${projectIdx}-${shiftIdx}-oot-block`}>
                                          {aiSuggested && (
                                            <div
                                              className="mb-2 rounded border-l-4 border-amber-500 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                                              data-testid={`ai-shift-${projectIdx}-${shiftIdx}-oot-suggestion`}
                                            >
                                              <div className="font-bold uppercase tracking-wider text-[9px] text-amber-700 mb-0.5">
                                                AI suggests Out of Town — please confirm
                                              </div>
                                              <div>
                                                {ootSugg.reason || 'AI thinks this shift may be out of town.'}
                                              </div>
                                            </div>
                                          )}
                                          <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const v = !!e.target.checked;
                                                updateShiftField(projectIdx, shiftIdx, 'out_of_town', v);
                                                updateShiftField(projectIdx, shiftIdx, 'user_confirmed_oot', v);
                                                updateShiftField(projectIdx, shiftIdx, 'oot_confirmed_at', v ? new Date().toISOString() : null);
                                                updateShiftField(projectIdx, shiftIdx, 'oot_confirmed_by', v ? null : null);
                                                // Clear advisory flag when user ticks; set it when
                                                // AI suggested but user did not confirm.
                                                updateShiftField(
                                                  projectIdx,
                                                  shiftIdx,
                                                  'ai_oot_suggested_but_not_confirmed',
                                                  !v && aiSuggested
                                                );
                                              }}
                                              data-testid={`ai-shift-${projectIdx}-${shiftIdx}-oot-toggle`}
                                              className="mt-0.5 h-3.5 w-3.5 accent-amber-600"
                                            />
                                            <span>
                                              <span className="font-bold uppercase tracking-wider text-[10px] text-slate-900">
                                                Out of Town (OOT)
                                              </span>
                                              <span className="block text-[10px] text-slate-500">
                                                Tick to add Travel Crew Time / Distance / Toll line items at create time.
                                              </span>
                                            </span>
                                          </label>
                                        </div>
                                      );
                                    })()}
                                    {/* EVENTSFLO-PROJECT-INTAKE-SHIFT-LOCATION-MODEL-AND-HARD-WARNING-COLOURS-V1
                                        Per-shift Venue + Location (within venue) + Description + Notes.
                                        - Shift Venue defaults from the Project Venue (matches.location_id).
                                        - Location dropdown is scoped to the selected shift Venue and supports
                                          inline "+ New Location" creation under that Venue. */}
                                    {(() => {
                                      const projectVenueId = project.matches?.location_id || null;
                                      // EVENTSFLO-AI-LOCATION-MATCHING-DIAGHILEV-V1
                                      // Pre-fill the shift's location from the project-level
                                      // venue_location match returned by the parser, so
                                      // "Area: Diaghilev" → "Diaghilev Hall" actually shows up
                                      // in the per-shift Location dropdown.
                                      const projectVenueLocationId = project.matches?.venue_location_id || null;
                                      const shiftVenueId = shift.venue_id || projectVenueId || '';
                                      // Auto-default shift.venue_id to project venue once available.
                                      if (!shift.venue_id && projectVenueId) {
                                        Promise.resolve().then(() => {
                                          if (!editedProjects[projectIdx]?.shifts?.[shiftIdx]?.venue_id) {
                                            updateShiftField(projectIdx, shiftIdx, 'venue_id', projectVenueId);
                                          }
                                        });
                                      }
                                      // Auto-default shift.venue_location_id from the parser's
                                      // best venue_location match, ONLY when the shift's venue
                                      // matches the project venue (don't cross-contaminate when
                                      // the user has overridden the venue).
                                      if (
                                        !shift.venue_location_id &&
                                        projectVenueLocationId &&
                                        (shift.venue_id || projectVenueId) === projectVenueId
                                      ) {
                                        Promise.resolve().then(() => {
                                          const cur = editedProjects[projectIdx]?.shifts?.[shiftIdx];
                                          if (cur && !cur.venue_location_id) {
                                            updateShiftField(projectIdx, shiftIdx, 'venue_location_id', projectVenueLocationId);
                                          }
                                        });
                                      }
                                      const shiftVenueName = (venues.find(v => v.id === shiftVenueId) || {}).name || '';
                                      const list = shiftVenueId ? venueLocationsByVenue[shiftVenueId] : [];
                                      if (shiftVenueId && list === undefined) ensureVenueLocations(shiftVenueId);
                                      const locItems = list || [];
                                      const selectedLocId = shift.venue_location_id || 'none';
                                      return (
                                        <div className="grid grid-cols-12 gap-2 items-start">
                                          <div className="col-span-3">
                                            <Label className="text-[10px] flex items-center gap-1">
                                              Venue
                                              {!shiftVenueId && (
                                                <Badge variant="outline" className="ml-1 text-[8px] text-yellow-300 bg-red-700 border-yellow-300 font-bold ring-1 ring-yellow-200/60 px-1 py-0">Required</Badge>
                                              )}
                                            </Label>
                                            <Select
                                              value={shiftVenueId || 'none'}
                                              onValueChange={(value) => {
                                                const v = value === 'none' ? null : value;
                                                updateShiftField(projectIdx, shiftIdx, 'venue_id', v);
                                                // Clear location when venue changes
                                                updateShiftField(projectIdx, shiftIdx, 'venue_location_id', null);
                                                if (v) ensureVenueLocations(v);
                                              }}
                                            >
                                              <SelectTrigger className={`mt-1 h-7 text-xs ${!shiftVenueId ? 'border-yellow-300 ring-1 ring-yellow-300' : ''}`}
                                                             data-testid={`project-${projectIdx}-shift-${shiftIdx}-venue-select`}>
                                                <SelectValue placeholder="Select venue..." />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none"><span className="text-muted-foreground">-- Select venue --</span></SelectItem>
                                                {venues.map(v => (
                                                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div className="col-span-4">
                                            <div className="flex items-center justify-between gap-1">
                                              <Label className="text-[10px] flex items-center gap-1">
                                                Location
                                                {!shift.venue_location_id && (
                                                  <Badge variant="outline" className="ml-1 text-[8px] text-yellow-300 bg-red-700 border-yellow-300 font-bold ring-1 ring-yellow-200/60 px-1 py-0">Required</Badge>
                                                )}
                                              </Label>
                                              {project.shifts.length > 1 && (
                                                <button
                                                  type="button"
                                                  className="text-[9px] uppercase tracking-wide text-cyan-300 hover:text-cyan-100 hover:underline disabled:opacity-40 disabled:no-underline"
                                                  disabled={!shift.venue_id && !shift.venue_location_id}
                                                  onClick={() => openApplyAll('location', projectIdx, shiftIdx)}
                                                  data-testid={`project-${projectIdx}-shift-${shiftIdx}-apply-location-all-btn`}
                                                  title="Apply this venue + location to all other shifts in this project"
                                                >
                                                  Apply to all
                                                </button>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-1 mt-1">
                                              <Select
                                                value={selectedLocId}
                                                onValueChange={(value) => updateShiftField(projectIdx, shiftIdx, 'venue_location_id', value === 'none' ? null : value)}
                                                disabled={!shiftVenueId}
                                              >
                                                <SelectTrigger className={`h-7 text-xs flex-1 ${!shift.venue_location_id ? 'border-yellow-300 ring-1 ring-yellow-300' : ''}`}
                                                               data-testid={`project-${projectIdx}-shift-${shiftIdx}-location-select`}>
                                                  <SelectValue placeholder={!shiftVenueId ? 'Pick venue first' : (locItems.length === 0 ? 'No locations — create one' : 'Select location...')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="none"><span className="text-muted-foreground">-- Select location --</span></SelectItem>
                                                  {locItems.map(vl => (
                                                    <SelectItem key={vl.id} value={vl.id}>
                                                      {vl.name}{vl.desc_short ? ` — ${vl.desc_short}` : ''}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-[10px] whitespace-nowrap shrink-0 px-1.5"
                                                disabled={!shiftVenueId}
                                                onClick={() => openCreateVenueLocation(projectIdx, shiftVenueId, shiftVenueName, '')}
                                                data-testid={`project-${projectIdx}-shift-${shiftIdx}-create-location-btn`}
                                                title={shiftVenueId ? `Create a new location under ${shiftVenueName || 'this venue'}` : 'Pick a venue first'}
                                              >
                                                <Plus className="w-3 h-3 mr-0.5" />
                                                New
                                              </Button>
                                            </div>
                                          </div>
                                          <div className="col-span-3">
                                            <Label className="text-[10px] flex items-center gap-1">
                                              Description
                                              {!(shift.description && shift.description.trim()) && (
                                                <Badge variant="outline" className="ml-1 text-[8px] text-yellow-300 bg-red-700 border-yellow-300 font-bold ring-1 ring-yellow-200/60 px-1 py-0">Required</Badge>
                                              )}
                                            </Label>
                                            <Input
                                              type="text"
                                              value={shift.description || ''}
                                              onChange={(e) => updateShiftField(projectIdx, shiftIdx, 'description', e.target.value)}
                                              placeholder="Short description of the work"
                                              className={`h-7 text-xs ${!(shift.description && shift.description.trim()) ? 'border-yellow-300 ring-1 ring-yellow-300' : ''}`}
                                              data-testid={`project-${projectIdx}-shift-${shiftIdx}-description`}
                                            />
                                          </div>
                                          <div className="col-span-2">
                                            <Label className="text-[10px]">Additional Notes</Label>
                                            <Input
                                              type="text"
                                              value={shift.notes || ''}
                                              onChange={(e) => updateShiftField(projectIdx, shiftIdx, 'notes', e.target.value)}
                                              placeholder="(e.g. déchargement/montage)"
                                              className="h-7 text-xs"
                                              data-testid={`project-${projectIdx}-shift-${shiftIdx}-notes`}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })}
                              
                              {(!project.shifts || project.shifts.length === 0) && (
                                <div className="text-center py-3 text-muted-foreground text-xs">
                                  No shifts. Click "Add" to add manually.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center text-muted-foreground">
              <p>No projects detected.</p>
              <Button onClick={parseWithAI} className="mt-4">
                <Bot className="w-4 h-4 mr-2" />
                Parse with AI
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-4 border-t border-white/10">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={parseWithAI} disabled={loading || creating}>
            <Bot className="w-4 h-4 mr-2" />
            Re-parse
          </Button>
          {/* EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1 */}
          <Button
            variant="outline"
            onClick={handlePreviewRules}
            disabled={selectedCount === 0 || loading || creating || rulePreviewLoading || createLocked}
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="ai-preview-rules-btn"
            title={createLocked ? 'Projects already created from this draft. Re-parse to start over.' : 'Dry-run: show which deterministic rules will fire on the selected projects/shifts before saving.'}
          >
            {rulePreviewLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Preview Rules
          </Button>
          <Button 
            onClick={handleCreateProjects}
            disabled={selectedCount === 0 || loading || creating || createLocked || hasUnpickedAlternate}
            className="bg-primary"
            data-testid="ai-create-projects-btn"
            title={createLocked
              ? 'Projects already created from this draft. Re-parse to start over.'
              : (hasUnpickedAlternate
                ? 'One or more rules are set to Alternate but have no replacement chosen.'
                : '')}
          >
            {creating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {createLocked
              ? 'Created — Re-parse to start over'
              : hasUnpickedAlternate
                ? 'Pick replacement(s) to enable Create'
                : `Create ${selectedCount} Project${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* EVENTSFLO-AI-PARSER-HELP-REPORT-RULE-PREVIEW-V1: pre-create rule preview drawer
        EVENTSFLO-RULE-PREVIEW-CONVERGE-AND-CONTRAST-V1: sharp high-contrast palette,
        no pastel opacity tints, matches the AI Parser warning idiom. */}
    <Dialog open={rulePreviewOpen} onOpenChange={(v) => !v && setRulePreviewOpen(false)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ai-rule-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-300" />
            Pre-create rule preview
          </DialogTitle>
          <DialogDescription>
            Deterministic rules expected to fire after creation. Generated entries are styled
            distinctly and labelled <em>Rule-based</em>. Final backend creation re-evaluates rules
            and is the authoritative result.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-1 space-y-3 text-sm" data-testid="ai-rule-preview-body">
          {rulePreview?.projects?.length === 0 && (
            <div className="text-muted-foreground italic">No projects selected for preview.</div>
          )}
          {(rulePreview?.projects || []).map((p, pi) => {
            const totalRulesForProject = (p.shifts || []).reduce((s, sh) => s + (sh.rules || []).length, 0);
            return (
              <div
                key={`prv-p-${pi}`}
                className="border border-slate-200 rounded-md bg-white text-slate-900 p-3 shadow-sm"
                data-testid={`ai-rule-preview-project-${pi}`}
              >
                <div className="font-bold uppercase tracking-wide text-slate-900 mb-1">
                  {p.project_name}
                  <span className="text-xs font-semibold text-slate-500 normal-case ml-2">
                    — {totalRulesForProject} rule-generated entr{totalRulesForProject === 1 ? 'y' : 'ies'} expected
                  </span>
                </div>
                <div className="space-y-2">
                  {(p.shifts || []).map((sh, si) => (
                    <div key={`prv-p-${pi}-sh-${si}`} className="ml-1">
                      <div className="text-xs text-slate-600">
                        <span className="font-semibold text-slate-900">[{sh.shift_label}]</span>
                        {' '}— crew {sh.staff_required}, {sh.duration_hours}h
                      </div>
                      {(sh.rules || []).length === 0 && (sh.advisories || []).length === 0 ? (
                        <div className="text-xs text-slate-500 italic ml-2">
                          No deterministic rules will fire for this shift.
                        </div>
                      ) : (
                        <>
                        {/* EVENTSFLO-OOT-MANUAL-CONFIRMATION-RULE-V1
                            Advisory cards render at the TOP of the rule list,
                            slate-bordered, info-only. No override controls. */}
                        {(sh.advisories || []).map((a, ai) => {
                          const origProjIdx = (previewSelectionMap[pi] !== undefined)
                            ? previewSelectionMap[pi]
                            : pi;
                          const origShiftIdx = sh.shift_index;
                          return (
                            <div
                              key={`adv-${pi}-${si}-${ai}`}
                              className="ml-3 mb-2 rounded-md bg-white border-l-4 border-slate-400 ring-1 ring-slate-200 px-3 py-2"
                              data-testid={`ai-rule-advisory-${origProjIdx}-${origShiftIdx}-${a.advisory_id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="text-[14px] font-bold text-slate-900">
                                    Advisory: AI suggestion (not confirmed)
                                  </div>
                                  <div className="text-[12px] mt-0.5 text-slate-600">
                                    {a.message}
                                  </div>
                                  {a.reason && (
                                    <div className="text-[11px] mt-0.5 text-slate-500 italic">
                                      Reason: {a.reason}
                                    </div>
                                  )}
                                </div>
                                <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                                  INFO
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        <ul className="ml-3 space-y-2">
                          {sh.rules.map((r, ri) => {
                            // EVENTSFLO-RULE-PREVIEW-UI-CORRECTION-V1
                            // Map preview project_index -> original editedProjects index
                            // using the snapshot taken at preview-fire time.
                            const origProjIdx = (previewSelectionMap[pi] !== undefined)
                              ? previewSelectionMap[pi]
                              : pi;
                            const origShiftIdx = sh.shift_index;
                            const ovKey = overrideKey(origProjIdx, origShiftIdx, r.rule_id);
                            const ov = ruleOverrides[ovKey] || {};
                            const action = ov.action || 'accept';

                            // Build alternate-task options (excluding the
                            // engine default itself so the dropdown shows
                            // ONLY actual replacements):
                            //  1. preview's `candidates` (only when ambiguous)
                            //  2. catalogue for r.supplement_group, lazy-loaded
                            const groupKey = r.supplement_group || null;
                            const groupCatalogue = groupKey
                              ? (supplementOptionsByGroup[groupKey] || [])
                              : [];
                            // EVENTSFLO-RULE-PREVIEW-MINIMAL-CONTRAST-AND-DEFAULT-FIX-V1
                            // Build alternates list. The engine default is
                            // ALWAYS the first row, labelled "(engine default)".
                            // Selecting it reverts the override to 'accept' so
                            // the user gets back to the canonical engine pick
                            // without losing context.
                            const ENGINE_DEFAULT_SENTINEL = '__ENGINE_DEFAULT__';
                            let altOptions = [];
                            const enginePool = (Array.isArray(r.candidates) && r.candidates.length > 0)
                              ? r.candidates
                              : groupCatalogue;
                            if (enginePool.length > 0) {
                              const others = enginePool
                                .filter(c => c && c.id !== r.task_definition_id)
                                .map(c => ({ id: c.id, name: c.name }));
                              if (r.predicted_task_name) {
                                altOptions.push({
                                  id: ENGINE_DEFAULT_SENTINEL,
                                  name: `${r.predicted_task_name} (engine default)`,
                                  isDefault: true,
                                });
                              }
                              altOptions.push(...others);
                            }
                            // Loading state for the catalogue (group set
                            // on rule but not yet fetched).
                            const groupLoading = !!groupKey
                              && supplementOptionsByGroup[groupKey] === undefined;
                            // Alternate is offered if the rule has at least
                            // one OTHER task (engine default alone doesn't
                            // count as an alternate).
                            const canAlternate = altOptions.filter(o => !o.isDefault).length > 0;

                            // Chosen alternate — DO NOT auto-promote. We only
                            // honour an explicit user pick stored in
                            // `ov.task_definition_id`. Empty string means
                            // "user has not chosen yet" → Create is blocked.
                            const chosenAltId = ov.task_definition_id || '';
                            const chosenAlt = altOptions.find(o => o.id === chosenAltId);
                            const chosenAltName = chosenAlt?.name || '';
                            const alternatePending = action === 'alternate' && !chosenAltId;

                            // === EVENTSFLO standard palette ===
                            // Single white card (no full-bleed colour fills),
                            // coloured LEFT STRIPE per state, ink-only state
                            // badge. WCAG-readable on any dialog background.
                            let cardCls;
                            let stateInkCls;
                            let stateBadgeLabel;
                            const bodyTextCls = 'text-slate-900';
                            const metaTextCls = 'text-slate-600';
                            let strikeOriginal = false;

                            if (action === 'ignore') {
                              cardCls = 'bg-white border-l-4 border-zinc-400 ring-1 ring-slate-200';
                              stateInkCls = 'text-zinc-500';
                              stateBadgeLabel = 'IGNORED';
                              strikeOriginal = true;
                            } else if (action === 'alternate') {
                              cardCls = 'bg-white border-l-4 border-amber-500 ring-1 ring-slate-200';
                              stateInkCls = 'text-amber-700';
                              stateBadgeLabel = 'SUBSTITUTED';
                              strikeOriginal = true;
                            } else if (r.ambiguous) {
                              cardCls = 'bg-white border-l-4 border-red-500 ring-1 ring-slate-200';
                              stateInkCls = 'text-red-700';
                              stateBadgeLabel = 'CONFIRM';
                            } else {
                              // Accept (default)
                              cardCls = 'bg-white border-l-4 border-emerald-500 ring-1 ring-slate-200';
                              stateInkCls = 'text-emerald-700';
                              stateBadgeLabel = 'ACCEPTED';
                            }
                            // Mute body text in the IGNORED state per spec.
                            const ignoredMuteCls = action === 'ignore' ? 'text-zinc-500' : '';

                            return (
                            <li
                              key={`prv-p-${pi}-sh-${si}-r-${ri}`}
                              className={'rounded-md px-3 py-2 ' + cardCls}
                              data-testid={`ai-rule-preview-rule-${pi}-${si}-${ri}`}
                              data-action={action}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className={'text-[14px] font-bold ' + (action === 'ignore' ? ignoredMuteCls : bodyTextCls)}>
                                    {r.rule_name}
                                  </div>
                                  <div className={'text-[12px] mt-0.5 ' + (action === 'ignore' ? ignoredMuteCls : metaTextCls)}>
                                    {r.message}
                                  </div>
                                </div>
                                <span
                                  className={'shrink-0 text-[10px] uppercase tracking-wider font-bold ' + stateInkCls}
                                  data-testid={`ai-rule-state-badge-${origProjIdx}-${origShiftIdx}-${r.rule_id}`}
                                >
                                  {stateBadgeLabel}
                                </span>
                              </div>

                              {/* What WILL the engine actually save? */}
                              {r.predicted_task_name && (
                                <div className={'text-[12px] mt-2 ' + (action === 'ignore' ? ignoredMuteCls : bodyTextCls)}>
                                  {action === 'alternate' ? (
                                    <>
                                      <span className="text-slate-600">Will add line item:</span>{' '}
                                      <span className="line-through text-slate-500">
                                        {r.predicted_task_name}
                                      </span>{' '}
                                      <span className="font-bold text-amber-700 mx-1">→</span>{' '}
                                      <strong
                                        className={alternatePending ? 'italic text-amber-700' : 'text-amber-700'}
                                        data-testid={`ai-rule-chosen-alt-name-${origProjIdx}-${origShiftIdx}-${r.rule_id}`}
                                      >
                                        {alternatePending ? '— pick a replacement —' : chosenAltName}
                                      </strong>
                                      <span className="ml-1 text-slate-600">
                                        {' × '}<strong>{r.predicted_quantity}</strong>
                                      </span>
                                    </>
                                  ) : action === 'ignore' ? (
                                    <>
                                      <span>Will NOT add:</span>{' '}
                                      <span className={strikeOriginal ? 'line-through' : ''}>
                                        {r.predicted_task_name}
                                      </span>
                                      <span> × {r.predicted_quantity}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-slate-600">Will add line item:</span>{' '}
                                      <strong className="text-emerald-700">{r.predicted_task_name}</strong>
                                      <span className="ml-1 text-slate-600">
                                        {' × '}<strong>{r.predicted_quantity}</strong>
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}

                              {/* EVENTSFLO-OOT-MANUAL-CONFIRMATION-RULE-V1
                                  Composite OOT rule renders its 3 subitems. */}
                              {Array.isArray(r.subitems) && r.subitems.length > 0 && action !== 'ignore' && (
                                <ul
                                  className="text-[11px] mt-1 ml-3 list-disc text-slate-700 space-y-0.5"
                                  data-testid={`ai-rule-subitems-${origProjIdx}-${origShiftIdx}-${r.rule_id}`}
                                >
                                  {r.subitems.map((si, sii) => (
                                    <li key={`subi-${sii}`} className="text-slate-700">
                                      <strong className="text-emerald-700">{si.name}</strong>
                                      <span className="text-slate-500"> — qty: {si.qty_formula}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {/* Override controls — Accept / Ignore / Alternate */}
                              <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-slate-200">
                                <span className="text-[10px] uppercase tracking-wider font-bold mr-1 text-slate-500">
                                  Override:
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className={'h-7 px-2.5 text-[11px] font-bold uppercase tracking-wide bg-white ' + (action === 'accept'
                                    ? 'text-emerald-700 border-emerald-500 ring-2 ring-emerald-200'
                                    : 'text-slate-700 border-slate-300 hover:bg-slate-50')}
                                  onClick={() => setOverride(origProjIdx, origShiftIdx, r.rule_id, { action: 'accept', task_definition_id: undefined })}
                                  data-testid={`ai-rule-override-${origProjIdx}-${origShiftIdx}-${r.rule_id}-accept`}
                                  aria-pressed={action === 'accept'}
                                >
                                  {action === 'accept' ? '✓ Accepted' : 'Accept'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className={'h-7 px-2.5 text-[11px] font-bold uppercase tracking-wide bg-white ' + (action === 'ignore'
                                    ? 'text-zinc-700 border-zinc-400 ring-2 ring-zinc-200'
                                    : 'text-slate-700 border-slate-300 hover:bg-slate-50')}
                                  onClick={() => setOverride(origProjIdx, origShiftIdx, r.rule_id, { action: 'ignore', task_definition_id: undefined })}
                                  data-testid={`ai-rule-override-${origProjIdx}-${origShiftIdx}-${r.rule_id}-ignore`}
                                  aria-pressed={action === 'ignore'}
                                >
                                  {action === 'ignore' ? '✓ Ignored' : 'Ignore'}
                                </Button>
                                {canAlternate ? (
                                  <>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className={'h-7 px-2.5 text-[11px] font-bold uppercase tracking-wide bg-white ' + (action === 'alternate'
                                        ? 'text-amber-700 border-amber-500 ring-2 ring-amber-200'
                                        : 'text-slate-700 border-slate-300 hover:bg-slate-50')}
                                      onClick={() => setOverride(origProjIdx, origShiftIdx, r.rule_id, { action: 'alternate', task_definition_id: undefined })}
                                      data-testid={`ai-rule-override-${origProjIdx}-${origShiftIdx}-${r.rule_id}-alternate`}
                                      aria-pressed={action === 'alternate'}
                                    >
                                      {action === 'alternate' ? '✓ Substituted' : 'Alternate'}
                                    </Button>
                                    {action === 'alternate' && (
                                      <Select
                                        value={chosenAltId || ''}
                                        onValueChange={(v) => {
                                          // Selecting the engine-default sentinel reverts to Accept.
                                          if (v === ENGINE_DEFAULT_SENTINEL) {
                                            setOverride(origProjIdx, origShiftIdx, r.rule_id, {
                                              action: 'accept',
                                              task_definition_id: undefined,
                                            });
                                          } else {
                                            setOverride(origProjIdx, origShiftIdx, r.rule_id, {
                                              action: 'alternate',
                                              task_definition_id: v,
                                            });
                                          }
                                        }}
                                      >
                                        <SelectTrigger
                                          className={'h-7 px-2 text-[11px] bg-white text-slate-900 w-auto min-w-[220px] font-semibold ' + (alternatePending
                                            ? 'border-2 border-amber-500 ring-2 ring-amber-200'
                                            : 'border border-slate-300')}
                                          data-testid={`ai-rule-override-${origProjIdx}-${origShiftIdx}-${r.rule_id}-select`}
                                        >
                                          <SelectValue placeholder="— pick a replacement —" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {altOptions.map(opt => (
                                            <SelectItem
                                              key={opt.id}
                                              value={opt.id}
                                              data-testid={
                                                opt.isDefault
                                                  ? `ai-rule-override-${origProjIdx}-${origShiftIdx}-${r.rule_id}-option-engine-default`
                                                  : undefined
                                              }
                                            >
                                              <span className={opt.isDefault ? 'font-bold text-emerald-700' : ''}>
                                                {opt.name}
                                              </span>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 text-[10px] italic text-slate-500 ml-1"
                                    data-testid={`ai-rule-no-alternate-${origProjIdx}-${origShiftIdx}-${r.rule_id}`}
                                    title={
                                      groupLoading
                                        ? 'Looking up alternative tasks…'
                                        : groupKey
                                          ? `No other active tasks share supplement_group "${groupKey}". Engine default is the only valid replacement.`
                                          : 'This rule’s engine task has no supplement_group, so no replacement set is defined. Configure tasks under a shared supplement_group to enable substitutions.'
                                    }
                                  >
                                    {groupLoading ? '⏳ Loading alternates…' : 'Alternate unavailable — no alt tasks'}
                                  </span>
                                )}
                                {alternatePending && (
                                  <span
                                    className="text-[10px] italic text-amber-700 ml-1"
                                    data-testid={`ai-rule-alternate-pending-${origProjIdx}-${origShiftIdx}-${r.rule_id}`}
                                  >
                                    Pick a replacement to enable Create
                                  </span>
                                )}
                              </div>

                              {/* Ambiguous: show other candidates only in default state. */}
                              {r.ambiguous && r.candidates && r.candidates.length > 0 && action === 'accept' && (
                                <div className="text-[11px] mt-1 font-semibold text-slate-600">
                                  Multiple matches:{' '}
                                  {r.candidates
                                    .filter(c => c.id !== r.task_definition_id)
                                    .map(c => c.name)
                                    .join(', ')}
                                </div>
                              )}
                            </li>
                            );
                          })}
                        </ul>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {rulePreview?.note && (
            <div className="text-[11px] italic text-muted-foreground border-t border-white/10 pt-2">
              {rulePreview.note}
            </div>
          )}
        </div>
        <DialogFooter className="pt-3 border-t border-white/10">
          <Button variant="outline" onClick={() => setRulePreviewOpen(false)} data-testid="ai-rule-preview-close">
            Close
          </Button>
          <Button
            onClick={() => { setRulePreviewOpen(false); handleCreateProjects(); }}
            disabled={selectedCount === 0 || creating || createLocked || hasUnpickedAlternate}
            className="bg-primary"
            data-testid="ai-rule-preview-confirm-create"
            title={createLocked
              ? 'Projects already created from this draft. Re-parse to start over.'
              : (hasUnpickedAlternate
                ? 'One or more rules are set to Alternate but have no replacement chosen.'
                : '')}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {createLocked
              ? 'Created — Re-parse to start over'
              : hasUnpickedAlternate
                ? 'Pick replacement(s) to enable Create'
                : `Create ${selectedCount} Project${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1
        Inline Create Client modal — minimal required fields + light optional fields. */}
    <Dialog open={!!createClientFor} onOpenChange={(v) => !v && !creatingEntity && setCreateClientFor(null)}>
      <DialogContent className="bg-card border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> Create Client
          </DialogTitle>
          <DialogDescription>
            Add a new client without leaving the draft. The new client will be auto-selected on this project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Name <span className="text-red-500">*</span></Label>
            <Input
              value={newClient.name}
              onChange={(e) => setNewClient((p) => ({ ...p, name: e.target.value }))}
              placeholder="Client name"
              className="mt-1 h-9 text-sm"
              data-testid="create-client-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contact email</Label>
              <Input
                type="email"
                value={newClient.contact_email}
                onChange={(e) => setNewClient((p) => ({ ...p, contact_email: e.target.value }))}
                placeholder="optional"
                className="mt-1 h-9 text-sm"
                data-testid="create-client-email"
              />
            </div>
            <div>
              <Label className="text-xs">Contact phone</Label>
              <Input
                value={newClient.contact_phone}
                onChange={(e) => setNewClient((p) => ({ ...p, contact_phone: e.target.value }))}
                placeholder="optional"
                className="mt-1 h-9 text-sm"
                data-testid="create-client-phone"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateClientFor(null)} disabled={creatingEntity}>
            Cancel
          </Button>
          <Button onClick={submitCreateClient} disabled={creatingEntity || !(newClient.name || '').trim()} data-testid="create-client-submit">
            {creatingEntity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating…</> : 'Create & Select'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1
        Inline Create Venue modal — name + address required. */}
    <Dialog open={!!createVenueFor} onOpenChange={(v) => !v && !creatingEntity && setCreateVenueFor(null)}>
      <DialogContent className="bg-card border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> Create Venue
          </DialogTitle>
          <DialogDescription>
            Add a new venue without leaving the draft. The new venue will be auto-selected on this project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Name <span className="text-red-500">*</span></Label>
            <Input
              value={newVenue.name}
              onChange={(e) => setNewVenue((p) => ({ ...p, name: e.target.value }))}
              placeholder="Venue name"
              className="mt-1 h-9 text-sm"
              data-testid="create-venue-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Address line 1 <span className="text-red-500">*</span></Label>
            <Input
              value={newVenue.address}
              onChange={(e) => setNewVenue((p) => ({ ...p, address: e.target.value }))}
              placeholder="Street / number"
              className="mt-1 h-9 text-sm"
              data-testid="create-venue-address"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">City</Label>
              <Input
                value={newVenue.city}
                onChange={(e) => setNewVenue((p) => ({ ...p, city: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="create-venue-city"
              />
            </div>
            <div>
              <Label className="text-xs">Postcode</Label>
              <Input
                value={newVenue.postcode}
                onChange={(e) => setNewVenue((p) => ({ ...p, postcode: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="create-venue-postcode"
              />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input
                value={newVenue.country}
                onChange={(e) => setNewVenue((p) => ({ ...p, country: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="create-venue-country"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateVenueFor(null)} disabled={creatingEntity}>
            Cancel
          </Button>
          <Button
            onClick={submitCreateVenue}
            disabled={creatingEntity || !(newVenue.name || '').trim() || !(newVenue.address || '').trim()}
            data-testid="create-venue-submit"
          >
            {creatingEntity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating…</> : 'Create & Select'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* EVENTSFLO-INBOX-FLAGS-WARNING-CONTRAST-AND-VENUE-LOCATION-FIX-V1
        Inline Create Location (under existing Venue) modal. */}
    <Dialog open={!!createVenueLocationFor} onOpenChange={(v) => !v && !creatingEntity && setCreateVenueLocationFor(null)}>
      <DialogContent className="bg-card border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> Create Location
          </DialogTitle>
          <DialogDescription>
            Create a new <strong>Location</strong> (meeting point) under the venue
            {createVenueLocationFor?.venueName ? <> <strong>{createVenueLocationFor.venueName}</strong></> : ''}.
            Locations always belong to a venue.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Location name <span className="text-red-500">*</span></Label>
            <Input
              value={newVenueLocation.name}
              onChange={(e) => setNewVenueLocation((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Backstage gate B, Loading bay 3, Stage Left"
              className="mt-1 h-9 text-sm"
              data-testid="create-venue-location-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Address line 1 (optional)</Label>
            <Input
              value={newVenueLocation.address1}
              onChange={(e) => setNewVenueLocation((p) => ({ ...p, address1: e.target.value }))}
              placeholder="If different from venue address"
              className="mt-1 h-9 text-sm"
              data-testid="create-venue-location-address1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Postcode</Label>
              <Input
                value={newVenueLocation.postcode}
                onChange={(e) => setNewVenueLocation((p) => ({ ...p, postcode: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="create-venue-location-postcode"
              />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input
                value={newVenueLocation.country}
                onChange={(e) => setNewVenueLocation((p) => ({ ...p, country: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="create-venue-location-country"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateVenueLocationFor(null)} disabled={creatingEntity}>
            Cancel
          </Button>
          <Button
            onClick={submitCreateVenueLocation}
            disabled={creatingEntity || !(newVenueLocation.name || '').trim()}
            data-testid="create-venue-location-submit"
          >
            {creatingEntity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating…</> : 'Create & Select'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* EVENTSFLO-AI-DRAFT-APPLY-TO-ALL-TASK-LOCATION-V1 */}
    <Dialog
      open={!!applyAllDialog}
      onOpenChange={(v) => !v && setApplyAllDialog(null)}
    >
      <DialogContent
        className="max-w-lg"
        data-testid="apply-to-all-dialog"
      >
        {applyAllDialog && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="apply-to-all-dialog-title">
                Apply {applyAllDialog.field === 'task' ? 'Task' : 'Venue + Location'} to all shifts
              </DialogTitle>
              <DialogDescription className="text-xs">
                Propagating value from shift #{applyAllDialog.sourceShiftIdx + 1}. The
                value is copied to the selected shifts only — match acceptance is
                not changed; you still review and accept each row.
              </DialogDescription>
            </DialogHeader>

            <div className="text-xs space-y-3">
              <div className="rounded border border-cyan-300/40 bg-cyan-500/10 p-2">
                <div className="text-[10px] uppercase tracking-wide text-cyan-200">
                  New value
                </div>
                <div
                  className="font-medium text-cyan-50 break-words"
                  data-testid="apply-to-all-new-value"
                >
                  {applyAllDialog.valueLabel}
                </div>
              </div>

              {(() => {
                const willChange = applyAllDialog.targets.filter((t) => !t.currentEqual);
                const willStay = applyAllDialog.targets.filter((t) => t.currentEqual);
                return (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                        Will change ({willChange.length})
                      </div>
                      {willChange.length === 0 ? (
                        <div
                          className="text-muted-foreground italic"
                          data-testid="apply-to-all-empty-change"
                        >
                          All other shifts already have this value.
                        </div>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                          {willChange.map((t) => (
                            <label
                              key={t.shiftIdx}
                              className="flex items-start gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-white/5"
                              data-testid={`apply-to-all-target-${t.shiftIdx}`}
                            >
                              <Checkbox
                                checked={t.selected}
                                onCheckedChange={() => toggleApplyTarget(t.shiftIdx)}
                                data-testid={`apply-to-all-target-${t.shiftIdx}-checkbox`}
                              />
                              <span className="flex-1">
                                <span className="font-medium">
                                  Shift #{t.shiftIdx + 1}
                                </span>
                                <span className="text-muted-foreground">
                                  : {t.currentLabel}{' '}
                                  <span className="text-cyan-300">
                                    → {applyAllDialog.valueLabel}
                                  </span>
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {willStay.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          Already equal — will stay ({willStay.length})
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto pr-1 opacity-60">
                          {willStay.map((t) => (
                            <div
                              key={t.shiftIdx}
                              className="text-[11px]"
                              data-testid={`apply-to-all-equal-${t.shiftIdx}`}
                            >
                              <span className="font-medium">
                                Shift #{t.shiftIdx + 1}
                              </span>
                              <span className="text-muted-foreground">
                                : {t.currentLabel}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setApplyAllDialog(null)}
                data-testid="apply-to-all-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApplyAll}
                disabled={
                  applyAllDialog.targets.filter((t) => t.selected && !t.currentEqual)
                    .length === 0
                }
                data-testid="apply-to-all-confirm-btn"
              >
                Apply to{' '}
                {
                  applyAllDialog.targets.filter((t) => t.selected && !t.currentEqual)
                    .length
                }{' '}
                shift
                {applyAllDialog.targets.filter((t) => t.selected && !t.currentEqual)
                  .length === 1
                  ? ''
                  : 's'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
